import _ from "lodash";
import client from "lib/apolloClient/client";
import update from "immutability-helper";
import {
  createOrganizationResourcesMutation,
  deleteOrganizationResourcesMutation,
} from "OrganizationResources/modules/OrganizationResourcesMutations";
import {
  createIBPYPElementRatingsMutation,
  updateStudentAssessmentMarksMutation,
} from "modules/CommonMutations";
import {
  getSelectedCurriculumId,
  getUserType,
  getSelectedAcademicYearId,
  getCurrentCurriculumProgramType,
  getIsStudent,
} from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { generateRandomId } from "Utils";
import {
  getAssignmentDetailsQuery,
  getStudentAssignmentQuery,
  getStudentAssignmentDetailsQuery,
} from "ClassRoom/modules/ClassRoomQuery";
import {
  getAssignmentDetailsQueryVariables,
  getIsStudentAssignmentRevampEnabled,
} from "ClassRoom/modules/utils";
import {
  getTimeTrackingEventsFromLocalStorage,
  removeTimeTrackingEventsFromLocalStorage,
} from "ClassRoom/modules/ClassRoomModule/ClassRoomUtils";
import {
  getStudentAssignmentDetailsFromCache,
  writeStudentAssignmentDetailsInCache,
  getStudentAssignmentFromCache,
  writeStudentAssignmentInCache,
} from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import {
  getAssessmentQuestionsQuery,
  getAssessmentQuestionsQueryV2,
  getSchoolQuestionLibraryFeedQuery,
  getSignedUrlsQuery,
  getStudentAssignmentQuestionSubmissionsQuery,
  getToddleQuestionLibraryFeedQuery,
  getToddleQuestionLibraryFeedQueryV2,
} from "./WorksheetQueries";
import {
  getAssessmentQuestionsQueryVariables,
  getUpdatedAssessmentQuestionsCacheData,
  getScoreObjFromSubmission,
  getReorderedWorksheetCacheData,
  getAddQuestionToLibraryVariables,
  updateQuestionMetadataInCache,
  getNextQuestionId,
  getWorksheetSectionsData,
  getAssessmentSubmissionQueryVariables,
  updateQuestionMetadataInCacheV2,
} from "./WorksheetUtils";
import {
  isWorksheetQueriesV2Enabled,
  getIsToddleLibraryWorksheetQueriesV2Enabled,
} from "./WorksheetFeatureFlags";
import {
  createQuestionMutation,
  updateOrganizationResourceMutation,
  updateQuestionMutation,
  updateQuestionSubmissionMutation,
  reorderOrganizationResourceMutation,
  createQuestionTopicMutation,
  addQuestionToSchoolLibraryMutation,
  importQuestionFromToddleLibraryMutation,
  importQuestionFromSchoolLibraryMutation,
  createWorksheetQuestionsUsingAI,
  duplicateQuestionInWorksheetMutation,
  createWorksheetSubmissionMutation,
  replaceWorksheetQuestionUsingAI,
  startSafeExamBrowserMutation,
  createQuestionMutationV2,
  reorderQuestionMutation,
  updateQuestionOrderMutation,
  createWorksheetQuestionUsingAIV2,
  deleteQuestionMutation,
  importQuestionFromSchoolLibraryMutationV2,
  importQuestionFromToddleLibraryMutationV2,
  duplicateQuestionInWorksheetV2Mutation,
  replaceWorksheetQuestionUsingAIV2Mutation,
  addBulkStudentResponseToPortfolioMutation,
  downloadBulkStudentResponseMutation,
  createEntityEventTrackingMutation,
  deleteAttachmentMutation,
} from "./WorksheetMutations";
import {
  getAssessmentQuestionsFromCache,
  writeAssessmentQuestionsInCache,
  getOrganizationResourceFromCache,
  writeOrganizationResourceInCache,
  getQuestionFromCache,
  writeQuestionInCache,
  getQuestionSubmissionFromCache,
  writeQuestionSubmissionInCache,
  getQuestionTopicsFromCache,
  writeQuestionTopicsInCache,
  getAssessmentQuestionsFromCacheV2,
  writeAssessmentQuestionsInCacheV2,
  getQuestionFromCacheV2,
  writeQuestionInCacheV2,
  getStudentAssignmentFromCacheV2,
  writeStudentAssignmentInCacheV2,
  getAIConversationsFromCache,
  writeAIConversationsInCache,
} from "./WorksheetGraphqlHelpers";

import { QUESTION_TOPIC } from "QuestionBank/modules/Constants";
import {
  CREATE_THROTTLED_CALL_TIME,
  SCHOOL_LIBRARY,
  TODDLE_LIBRARY,
  METADATA_ADDED_TO_LIBRARY_KEY,
  QUESTION_FEED_TYPE_ALL_QUESTIONS,
  AI_GEN_IDENTIFIER,
} from "Worksheet/modules/WorksheetConstants";
import {
  TODDLE_LIBRARY as ACTIVE_TAB_TODDLE_LIBRARY,
  WORKSHEET_QUESTION_CREATION,
} from "Constants/stringConstants";
import { Sentry } from "services/sentry";
import ACLStore from "lib/aclStore";
import { getPlannerTemplateQuery } from "modules/CommonQuery";
import {
  isPortfolioParityEnabled,
  getNestedTaggedLearningGoalsIds,
} from "Post/utils";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getCourseDetails } from "Post/modules/PostModule";
import {
  getIsCourseBasedGradingEnabled,
  getIsSubjectBasedGradingEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";
import { checkIsDateSame } from "Utils/DateTimeHelpers";

/** THUNKS */

export const fetchPlannerTemplate = ({ templateId }) => {
  return async dispatch => {
    if (!templateId) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return { successful: false };
    }

    try {
      const variables = {
        ids: [templateId],
      };

      const response = await client.query({
        query: getPlannerTemplateQuery,
        variables: variables,
        fetchPolicy: "network-only",
      });

      const multiNode = _.get(response, "data.multiNode", []);
      const templateNode = _.find(multiNode, node => node.id === templateId);

      if (!templateNode) {
        return { successful: false };
      }

      const field_list = _.get(templateNode, "body.field_list", {});

      return {
        successful: true,
        data: {
          field_list,
          template: _.get(templateNode, "body", {}),
        },
      };
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return { successful: false };
    }
  };
};

/**
 * @deprecated use createQuestionV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const createParentResource = ({
  parentId,
  parentType,
  organizationResources,
}) => {
  return async dispatch => {
    dispatch(updateLoadingState(true));

    const variables = {
      parentId,
      parentType,
      organizationResources,
      portalType: "PLANNER",
    };

    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId,
        parentType,
        portalType: "PLANNER",
      }
    );
    try {
      const response = await client.mutate({
        mutation: createOrganizationResourcesMutation,
        variables,
        refetchQueries: [
          {
            query: getAssessmentQuestionsQuery,
            variables: assessmentQuestionsQueryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });

      const parentResourceId = _.get(
        response,
        "data.platform.createOrganizationResources[0].id",
        null
      );

      return parentResourceId;
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    } finally {
      dispatch(updateLoadingState(false));
    }
  };
};

export const fetchRandomQuestions = ({ variables, libraryType }) => {
  return async dispatch => {
    try {
      if (
        _.isEmpty(_.get(variables, "filters.grades", [])) &&
        libraryType === TODDLE_LIBRARY
      ) {
        throw new Error("grades are empty");
      }
      const toddleLibraryQuery = getIsToddleLibraryWorksheetQueriesV2Enabled()
        ? getToddleQuestionLibraryFeedQueryV2
        : getToddleQuestionLibraryFeedQuery;
      const data = await client.query({
        query:
          libraryType === SCHOOL_LIBRARY
            ? getSchoolQuestionLibraryFeedQuery
            : toddleLibraryQuery,
        variables: variables,
        fetchPolicy: "network-only",
      });
      return _.get(data, ["data", "node"]);
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * @deprecated use createQuestionV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const createSection = ({
  parentResourceId,
  parentId,
  parentType,
  organizationResources,
  putAfterItemId = null,
}) => {
  return async dispatch => {
    dispatch(updateLoadingState(true));

    const variables = {
      parentId,
      parentResourceId,
      parentType,
      organizationResources,
      shouldHandleSequence: true,
      putAfterItemId,
      portalType: "PLANNER",
    };

    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId,
        parentType,
        portalType: "PLANNER",
      }
    );

    const cacheData = getAssessmentQuestionsFromCache(
      assessmentQuestionsQueryVariables
    );

    // * If organization resources are empty, trigger Sentry for it.
    if (_.isEmpty(_.get(cacheData, "organizationResourcesV2.edges", []))) {
      Sentry.captureMessage(
        "Organization resources are empty in createSection",
        {
          level: "error",
          extra: {
            assessmentQuestionsData: cacheData,
            assessmentQuestionsQueryVariables: assessmentQuestionsQueryVariables,
          },
        }
      );
    }

    const updatedCacheData = getUpdatedAssessmentQuestionsCacheData({
      cacheData,
      variables,
    });

    writeAssessmentQuestionsInCache(
      assessmentQuestionsQueryVariables,
      updatedCacheData
    );

    try {
      const res = await client.mutate({
        mutation: createOrganizationResourcesMutation,
        variables,
        refetchQueries: [
          {
            query: getAssessmentQuestionsQuery,
            variables: assessmentQuestionsQueryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });

      const sectionId = _.get(
        res,
        "data.platform.createOrganizationResources[0].id",
        ""
      );

      dispatch(updateLoadingState(false));
      return sectionId;
    } catch (e) {
      dispatch(updateLoadingState(false));
      writeAssessmentQuestionsInCache(assessmentQuestionsQueryVariables, {
        node: cacheData,
      });

      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * @deprecated use createQuestionV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const createQuestion = ({
  parentResourceId,
  questionTypeId,
  organizationResources,
  putAfterItemId = null,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    if (!_.isNil(parentId) && !_.isNil(parentType)) {
      dispatch(updateLoadingState(true));

      const variables = {
        input: {
          questionTypeId,
          organizationResource: {
            parentId,
            parentResourceId,
            parentType,
            organizationResources,
            shouldHandleSequence: true,
            putAfterItemId,
          },
        },
        portalType: "PLANNER",
      };

      const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
        {
          parentId,
          parentType,
          portalType: "PLANNER",
        }
      );

      try {
        const res = await client.mutate({
          mutation: createQuestionMutation,
          variables,
          refetchQueries: [
            {
              query: getAssessmentQuestionsQuery,
              variables: assessmentQuestionsQueryVariables,
            },
          ],
          awaitRefetchQueries: true,
        });
        const newQuestionId = _.get(res, "data.content.createQuestion.id", "");

        dispatch(updateActiveQuestion(newQuestionId));
      } catch (e) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      } finally {
        dispatch(updateLoadingState(false));
      }
    }
  };
};

/**
 * Creates a new question in the worksheet.
 *
 * @param {Object} params - The parameters object
 * @param {string} params.parentId - ID of the parent entity (section/item)
 * @param {string} params.parentType - Type of the parent entity
 * @param {string} params.questionTypeId - ID of the question type to create
 * @param {Array} [params.fields=[]] - Array of field objects to initialize the question with
 * @param {string|null} [params.putAfterQuestionId=null] - ID of the question after which to insert the new question
 * @param {boolean} [params.isQueriesV2Enabled=false] - Whether to use V2 GraphQL queries
 * @param {boolean} [params.setQuestionAsActive=true] - Whether to set the newly created question as active
 * @param {boolean} [params.onlyCheckParentEntities=false] - If true, only validates parentId/parentType instead of requiring assessmentId/assessmentType from Redux state. Used in CreateAssignmentModal where Redux state is not yet initialized.
 * @returns {Function} Redux thunk that creates the question and returns its ID
 */
export const createQuestionV2 = ({
  parentId,
  parentType,
  questionTypeId,
  fields,
  putAfterQuestionId = null,
  isQueriesV2Enabled = false,
  setQuestionAsActive = true,
  onlyCheckParentEntities = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const assessmentId = _.get(state, "worksheet.parentEntity.id", null);
    const assessmentType = _.get(state, "worksheet.parentEntity.type", null);

    // Ensure all required IDs are available
    if (
      onlyCheckParentEntities
        ? !_.isNil(parentId) && !_.isNil(parentType)
        : !_.isNil(assessmentId) &&
          !_.isNil(assessmentType) &&
          !_.isNil(parentId) &&
          !_.isNil(parentType)
    ) {
      dispatch(updateLoadingState(true));

      const variables = {
        input: {
          parentId,
          parentType,
          questionTypeId,
          putAfterQuestionId,
          fields,
        },
        portalType: "PLANNER",
      };
      const refetchQueries = [];

      if (isQueriesV2Enabled) {
        // Prepare query variables for refetching assessment questions after mutation
        const assessmentQuestionsQueryVariables = {
          id: assessmentId || parentId,
          type: assessmentType || parentType,
          portalType: "PLANNER",
        };

        refetchQueries.push({
          query: getAssessmentQuestionsQueryV2,
          variables: assessmentQuestionsQueryVariables,
        });
      } else {
        //WORKSHEET_RESTRUCTURING: Remove this block when queriesV2 is enabled
        // Prepare query variables for refetching assessment questions after mutation
        const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
          {
            parentId: assessmentId || parentId,
            parentType: assessmentType || parentType,
            portalType: "PLANNER",
          }
        );
        refetchQueries.push({
          query: getAssessmentQuestionsQuery,
          variables: assessmentQuestionsQueryVariables,
        });
      }

      try {
        // Execute the mutation to create the question
        const res = await client.mutate({
          mutation: createQuestionMutationV2,
          variables,
          refetchQueries,
          awaitRefetchQueries: true,
        });

        const newQuestionId = _.get(
          res,
          "data.content.createQuestionV2.id",
          ""
        );

        // Set the active question to the newly created one
        if (setQuestionAsActive) {
          dispatch(updateActiveQuestion(newQuestionId));
        }

        return newQuestionId;
      } catch (e) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      } finally {
        dispatch(updateLoadingState(false));
      }
    }
  };
};

/**
 * @deprecated Use `updateSectionV2` instead.
 * This function is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const updateSection = input => {
  return async dispatch => {
    const variables = {
      input,
      portalType: "PLANNER",
    };

    try {
      await client.mutate({
        mutation: updateOrganizationResourceMutation,
        variables,
      });
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * Updates a worksheet section using the provided input and dispatches a toast message on error.
 * Currenty used to update the title of the section.
 */
export const updateSectionV2 = input => {
  return async dispatch => {
    const variables = {
      input,
      portalType: "PLANNER",
    };

    try {
      await client.mutate({
        mutation: updateQuestionMutation,
        variables,
      });
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/** Mutation for updating question */
export const updateQuestion = ({
  id = null,
  questionData = {},
  fields = [],
  removedFields = [],
  refetchQueries = [],
  awaitRefetchQueries = false,
}) => {
  if (_.includes(id, AI_GEN_IDENTIFIER)) {
    return;
  }
  return async (dispatch, getState) => {
    const state = getState();

    const questionId = _.isNil(id)
      ? _.get(state, "worksheet.activeQuestion")
      : id;
    const input = {};

    if (!_.isEmpty(questionData)) {
      input.questionData = questionData;
    }

    if (!_.isEmpty(fields)) {
      input.fields = fields;
    }

    if (!_.isEmpty(removedFields)) {
      input.removedFields = removedFields;
    }

    if (!_.isEmpty(input)) {
      input.questionId = questionId;

      if (_.startsWith(questionId, "NEW_")) {
        Sentry.captureMessage(`Invalid Question ID`, {
          level: "error",
          tags: {
            component: "WorksheetModules",
          },
          extra: {
            functionName: "updateQuestion",
            input: JSON.stringify(input),
          },
        });
        return;
      }

      try {
        await client.mutate({
          mutation: updateQuestionMutation,
          variables: { input, portalType: "PLANNER" },
          refetchQueries,
          awaitRefetchQueries,
        });
      } catch (e) {
        if (e.networkError) {
          dispatch(setToastMsg("toastMsgs:no_internet_connection"));
        } else {
          dispatch(setToastMsg("toastMsgs:something_went_wrong"));
          e.portalType = "PLANNER";
          e.input = input;
          throw e;
        }
        throw e;
      }
    }
  };
};

/** Mutation for updating question submission  */
export const updateQuestionSubmission = ({
  id = null,
  fields = [],
  refetchQueries = [],
  awaitRefetchQueries = false,
  fetchFields = false,
  questionId,
  questionTypeUid,
  fieldUid,
  mode,
}) => {
  return async (dispatch, getState) => {
    //this might occur if teacher edited worksheet & submission item is not created
    const state = getState();
    const userType = getUserType(state);

    const assessmentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentId = _.get(state, "worksheet.parentSubmissionId", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId: assessmentId,
        parentType,
        portalType: "PLANNER",
      }
    );

    const variables = getAssessmentSubmissionQueryVariables({
      submissionId: parentId,
      mode: mode,
    });
    const input = {
      questionSubmissionId: id,
    };

    if (!_.isEmpty(fields)) {
      input.fields = fields;
    }

    if (!_.isEmpty(input)) {
      try {
        if (_.isNil(id)) {
          Sentry.captureMessage(`Question Submission ID Empty`, {
            level: "error",
            tags: {
              component: "WorksheetModules",
            },
            extra: {
              functionName: "updateQuestionSubmission",
              questionId,
              questionTypeUid,
              input: JSON.stringify(input),
              fieldUid,
            },
          });
        } else if (_.isNil(_.get(_.first(fields), "id"))) {
          Sentry.captureMessage(`Question Submission Fields ID undefined`, {
            level: "error",
            tags: {
              component: "WorksheetModules",
            },
            extra: {
              functionName: "updateQuestionSubmission",
              input: JSON.stringify(input),
              questionSubmissionId: id,
              questionId,
              questionTypeUid,
              fieldUid,
            },
          });
          throw new Error();
        }
        const res = await client.mutate({
          mutation: updateQuestionSubmissionMutation,
          variables: { input, fetchFields },
          refetchQueries,
          awaitRefetchQueries,
        });
        return res;
      } catch (e) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
        if (_.isEqual(userType, "student"))
          dispatch(
            createWorksheetSubmission({
              assessmentId,
              parentId,
              isFromUpdateQuestionSubmission: true, // to check whether createWorksheetSubmission is called while updatingQuestionSubmission
              refetchQueries: [
                {
                  query: getAssessmentQuestionsQuery,
                  variables: assessmentQuestionsQueryVariables,
                },
                {
                  query: getStudentAssignmentQuestionSubmissionsQuery,
                  variables: variables,
                },
              ],
            })
          );

        throw e;
      }
    }
  };
};

/**
 * @deprecated use deleteQuestionFieldV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const deleteQuestionField = ({
  ids = [],
  isAnswerField = false,
  questionId,
}) => {
  return async dispatch => {
    const cacheData = getQuestionFromCache({
      id: questionId,
    });

    const fieldsKey = isAnswerField ? "answerFields" : "fields";

    const fields = _.get(cacheData, fieldsKey);
    const updatedFields = _.filter(
      fields,
      field => !_.includes(ids, _.get(field, "id"))
    );

    const updatedCacheData = update(cacheData, {
      [fieldsKey]: {
        $set: updatedFields,
      },
    });

    writeQuestionInCache({
      id: questionId,
      data: updatedCacheData,
    });

    try {
      await dispatch(updateQuestion({ id: questionId, removedFields: ids }));
    } catch (e) {
      writeQuestionInCache({
        id: questionId,
        data: cacheData,
      });
    }
  };
};

/**
 * @deprecated use createQuestionFieldV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const createQuestionField = ({ uids = [], portalType, questionId }) => {
  return async (dispatch, getState) => {
    dispatch(updateLoadingState(true));
    const state = getState();

    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    if (!_.isNil(parentId) && !_.isNil(parentType)) {
      const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
        {
          parentId,
          parentType,
          portalType,
        }
      );

      const fields = _.map(uids, uid => ({ uid }));

      try {
        await dispatch(
          updateQuestion({
            id: questionId,
            fields: fields,
            refetchQueries: [
              {
                query: getAssessmentQuestionsQuery,
                variables: assessmentQuestionsQueryVariables,
              },
            ],
            awaitRefetchQueries: true,
          })
        );
      } catch {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      } finally {
        dispatch(updateLoadingState(false));
      }
    }
  };
};

/**
 * @description Deletes the question's item
 */
export const deleteQuestion = ({
  questionId,
  parentId,
  isSubQuestion = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const activeQuestionId = _.get(state, "worksheet.activeQuestion");
    const cacheData = getOrganizationResourceFromCache(parentId, isSubQuestion);

    const assessmentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);
    if (!_.isNil(assessmentId) && !_.isNil(parentType)) {
      const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
        {
          parentId: assessmentId,
          parentType,
          portalType: "PLANNER",
        }
      );
      const cacheData = getAssessmentQuestionsFromCache(
        assessmentQuestionsQueryVariables
      );
      const sections = getWorksheetSectionsData(cacheData);
      const nextQuestionId = getNextQuestionId({
        sections,
        questionId,
        isSubQuestion,
      });
      !_.isEmpty(nextQuestionId)
        ? dispatch(updateActiveQuestion(nextQuestionId))
        : dispatch(updateActiveQuestion(WORKSHEET_QUESTION_CREATION));
    }

    const questionIndex = _.findIndex(
      _.get(cacheData, "childrenV2.edges", []),
      data => {
        return _.get(data, "node.item.id") == questionId;
      }
    );

    // This code will be removed when we migrate to the new GraphQL Query API (v2)
    const updateCacheData = update(cacheData, {
      childrenV2: {
        resourceTypeBasedCount: {
          totalCount: {
            $set:
              _.get(cacheData, "childrenV2.resourceTypeBasedCount.totalCount") -
              1,
          },
        },
        edges: { $splice: [[questionIndex, 1]] },
      },
    });

    writeOrganizationResourceInCache(parentId, updateCacheData, isSubQuestion);

    try {
      const response = await client.mutate({
        mutation: deleteQuestionMutation,
        variables: {
          id: questionId,
          portalType: "PLANNER",
        },
      });
      const isSuccessful = _.get(
        response,
        "data.platform.deleteOrganizationResource",
        false
      );
      if (!isSuccessful) {
        dispatch(updateActiveQuestion(activeQuestionId));
      }
    } catch (e) {
      writeOrganizationResourceInCache(parentId, cacheData, isSubQuestion);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * @deprecated use deleteQuestion instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 *
 * @description Deletes the question's organization resource item
 */
export const deleteQuestionOrganizationResource = ({
  id,
  parentId,
  isSubQuestion = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const activeQuestionId = _.get(state, "worksheet.activeQuestion");
    const cacheData = getOrganizationResourceFromCache(parentId, isSubQuestion);

    const assessmentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);
    if (!_.isNil(assessmentId) && !_.isNil(parentType)) {
      const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
        {
          parentId: assessmentId,
          parentType,
          portalType: "PLANNER",
        }
      );
      const cacheData = getAssessmentQuestionsFromCache(
        assessmentQuestionsQueryVariables
      );
      const sections = getWorksheetSectionsData(cacheData);
      const nextQuestionId = getNextQuestionId({
        sections,
        questionId: id,
        isSubQuestion,
      });
      !_.isEmpty(nextQuestionId)
        ? dispatch(updateActiveQuestion(nextQuestionId))
        : dispatch(updateActiveQuestion(WORKSHEET_QUESTION_CREATION));
    }

    const questionIndex = _.findIndex(
      _.get(cacheData, "childrenV2.edges", []),
      data => {
        return _.get(data, "node.id") == id;
      }
    );

    const updateCacheData = update(cacheData, {
      childrenV2: {
        resourceTypeBasedCount: {
          totalCount: {
            $set:
              _.get(cacheData, "childrenV2.resourceTypeBasedCount.totalCount") -
              1,
          },
        },
        edges: { $splice: [[questionIndex, 1]] },
      },
    });

    writeOrganizationResourceInCache(parentId, updateCacheData, isSubQuestion);

    try {
      const response = await client.mutate({
        mutation: deleteOrganizationResourcesMutation,
        variables: {
          ids: [id],
          portalType: "PLANNER",
        },
      });
      const isSuccessful = _.get(
        response,
        "data.platform.deleteOrganizationResource",
        false
      );
      if (!isSuccessful) {
        dispatch(updateActiveQuestion(activeQuestionId));
      }
    } catch (e) {
      writeOrganizationResourceInCache(parentId, cacheData, isSubQuestion);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const createWorksheetSubmission = ({
  assessmentId,
  parentId,
  refetchQueries = [],
  submission,
  isFromUpdateQuestionSubmission = false,
}) => {
  return async dispatch => {
    if (_.isEmpty(assessmentId) || _.isEmpty(parentId)) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      Sentry.captureMessage(
        "Empty assessmentId or parentId in createWorksheetSubmission",
        {
          level: "error",
          extra: {
            assessmentId,
            parentId,
            isFromUpdateQuestionSubmission,
            submission,
          },
        }
      );
      return;
    }
    try {
      await client.mutate({
        mutation: createWorksheetSubmissionMutation,
        variables: {
          assessmentId,
          parentId,
          userFilter: "STUDENT",
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const dispatchCreateWorksheetSubmission = (
  dispatch,
  assessmentId,
  parentId,
  refetchQueries
) => {
  return dispatch(
    createWorksheetSubmission({ assessmentId, parentId, refetchQueries })
  );
};

export const throttledCreateWorksheetSubmission = _.throttle(
  dispatchCreateWorksheetSubmission,
  CREATE_THROTTLED_CALL_TIME,
  { trailing: false }
);

export const throttledCreateWorksheetSubmissionWrapper = ({
  assessmentId,
  parentId,
  refetchQueries = [],
}) => {
  return async dispatch => {
    throttledCreateWorksheetSubmission(
      dispatch,
      assessmentId,
      parentId,
      refetchQueries
    );
  };
};

/**
 * @deprecated use deleteQuestion instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const deleteSection = ({ id }) => {
  return async (dispatch, getState) => {
    const state = getState();

    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId,
        parentType,
        portalType: "PLANNER",
      }
    );

    const cacheData = getAssessmentQuestionsFromCache(
      assessmentQuestionsQueryVariables
    );

    // * Get all sections
    const sectionEdges = _.get(
      cacheData,
      "organizationResourcesV2.edges[0].node.childrenV2.edges",
      []
    );
    if (_.isEmpty(sectionEdges)) {
      // * if section edges are empty, trigger Sentry for it.
      Sentry.captureMessage(new Error("DELETING_SECTION_FROM_EMPTY_LIST"), {
        level: "info",
        extra: { data: cacheData, deletedSectionId: id, parentId },
      });

      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return;
    }

    // * Filter out the deleted section
    const filteredEdges = _.filter(sectionEdges, resource => {
      return _.get(resource, "node.id") != id;
    });

    const updatedCacheData = {
      node: update(cacheData, {
        organizationResourcesV2: {
          edges: {
            [0]: {
              node: {
                childrenV2: {
                  edges: {
                    $set: filteredEdges,
                  },
                },
              },
            },
          },
        },
      }),
    };

    writeAssessmentQuestionsInCache(
      assessmentQuestionsQueryVariables,
      updatedCacheData
    );

    try {
      const refetchQueries = [
        {
          query: getAssessmentQuestionsQuery,
          variables: assessmentQuestionsQueryVariables,
        },
      ];

      await client.mutate({
        mutation: deleteOrganizationResourcesMutation,
        variables: {
          ids: [id],
          portalType: "PLANNER",
        },
        refetchQueries,
      });
    } catch (e) {
      writeAssessmentQuestionsInCache(
        assessmentQuestionsQueryVariables,
        cacheData
      );
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteSectionV2 = ({ questionId }) => {
  return async (dispatch, getState) => {
    const state = getState();

    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId,
        parentType,
        portalType: "PLANNER",
      }
    );

    const cacheData = getAssessmentQuestionsFromCache(
      assessmentQuestionsQueryVariables
    );

    // This code will be removed when we migrate to the new GraphQL Query API (v2)
    const updatedCacheData = {
      node: update(cacheData, {
        organizationResourcesV2: {
          edges: {
            [0]: {
              node: {
                childrenV2: {
                  edges: {
                    $set: _.filter(
                      _.get(
                        cacheData,
                        "organizationResourcesV2.edges[0].node.childrenV2.edges",
                        []
                      ),
                      resource => {
                        return _.get(resource, "node.item.id") != questionId;
                      }
                    ),
                  },
                },
              },
            },
          },
        },
      }),
    };

    writeAssessmentQuestionsInCache(
      assessmentQuestionsQueryVariables,
      updatedCacheData
    );

    try {
      await client.mutate({
        mutation: deleteQuestionMutation,
        variables: {
          id: questionId,
          portalType: "PLANNER",
        },
      });
    } catch (e) {
      writeAssessmentQuestionsInCache(
        assessmentQuestionsQueryVariables,
        cacheData
      );
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * Deletes a question or section from the worksheet using V2 flat data structure.
 * This function follows the same pattern as reorderQuestionV2 but handles deletion
 * instead of reordering.
 *
 * Key features:
 * - Uses flat question structure for better performance
 * - Handles both question and section deletion
 * - Updates active question selection when necessary
 * - Optimistic cache updates with error recovery
 * - Works with flat question arrays instead of hierarchical cache data
 * - Removes deleted item and updates sequence for remaining items in same parent
 */
export const deleteQuestionV2 = ({ questionId }) => {
  return async (dispatch, getState) => {
    const state = getState();

    const assessmentId = _.get(state, "worksheet.parentEntity.id", null);
    const assessmentType = _.get(state, "worksheet.parentEntity.type", null);
    const activeQuestionId = _.get(state, "worksheet.activeQuestion");

    // Use V2 query variables structure (flat data)
    const assessmentQuestionsQueryVariables = {
      id: assessmentId,
      type: assessmentType,
      portalType: "PLANNER",
    };

    // Get current data from cache - using V2 structure with flat questions
    const cacheData = getAssessmentQuestionsFromCacheV2(
      assessmentQuestionsQueryVariables
    );

    if (!cacheData) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return;
    }

    // Work with flat questions array instead of hierarchical structure
    const flatQuestions = _.get(cacheData, "questions.edges", []);

    if (!_.isArray(flatQuestions)) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return;
    }

    // Find the item being deleted (could be question or section)
    const deletingItem = _.find(
      flatQuestions,
      q => _.get(q, "id") === questionId
    );

    if (!deletingItem) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return;
    }

    // Determine if the item being deleted is a section
    const isSection = _.get(deletingItem, "questionType.uid") === "section";
    const deletingItemParentId = _.get(deletingItem, "parentIdV2");

    // Handle active question update FIRST - before any cache operations
    if (activeQuestionId === questionId || isSection) {
      // Find next active question
      let nextActiveQuestionId = null;

      if (isSection) {
        // If deleting a section, find questions in other sections
        const remainingQuestions = _.filter(
          flatQuestions,
          q =>
            _.get(q, "id") !== questionId &&
            _.get(q, "questionType.uid") !== "section" &&
            _.get(q, "parentIdV2") !== questionId
        );

        if (remainingQuestions.length > 0) {
          // Sort by sequence and pick the first one
          const sortedQuestions = _.sortBy(remainingQuestions, "sequence");
          nextActiveQuestionId = _.get(sortedQuestions[0], "id");
        }
      } else {
        // If deleting a question, find next question in same section or other sections
        const sectionQuestions = _.filter(
          flatQuestions,
          q =>
            _.get(q, "parentIdV2") === deletingItemParentId &&
            _.get(q, "id") !== questionId &&
            _.get(q, "questionType.uid") !== "section"
        );

        if (sectionQuestions.length > 0) {
          // Find next question in same section by sequence
          const sortedSectionQuestions = _.sortBy(sectionQuestions, "sequence");
          // Just pick the first available question in the section
          nextActiveQuestionId = _.get(sortedSectionQuestions[0], "id");
        } else {
          // No questions in same section, find questions in other sections
          const otherQuestions = _.filter(
            flatQuestions,
            q =>
              _.get(q, "parentIdV2") !== deletingItemParentId &&
              _.get(q, "questionType.uid") !== "section"
          );

          if (otherQuestions.length > 0) {
            const sortedOtherQuestions = _.sortBy(otherQuestions, "sequence");
            nextActiveQuestionId = _.get(sortedOtherQuestions[0], "id");
          }
        }
      }

      // Update active question immediately and synchronously
      if (nextActiveQuestionId) {
        dispatch(updateActiveQuestion(nextActiveQuestionId));
      } else {
        // No questions remaining, go to creation mode
        dispatch(updateActiveQuestion(WORKSHEET_QUESTION_CREATION));
      }
    }

    // Create a copy of flatQuestions to modify
    let updatedQuestions = _.cloneDeep(flatQuestions);

    if (isSection) {
      // SECTION DELETION: Remove section and all its child questions

      // Filter out the section and all questions that belong to it
      updatedQuestions = _.filter(updatedQuestions, q => {
        // Remove the section itself
        if (_.get(q, "id") === questionId) {
          return false;
        }
        // Remove all questions that belong to this section
        if (_.get(q, "parentIdV2") === questionId) {
          return false;
        }
        return true;
      });

      // Recalculate sequences for remaining sections (items with parentTypeV2 !== "ASSESSMENT")
      const remainingSections = _.filter(
        updatedQuestions,
        q =>
          _.get(q, "questionType.uid") === "section" &&
          _.get(q, "parentTypeV2") !== "ASSESSMENT"
      );

      const sortedSections = _.sortBy(remainingSections, "sequence");

      // Update sequences in the main array for all sections
      _.forEach(sortedSections, (section, index) => {
        const mainArrayIndex = _.findIndex(
          updatedQuestions,
          q => _.get(q, "id") === _.get(section, "id")
        );
        if (mainArrayIndex !== -1) {
          _.set(updatedQuestions[mainArrayIndex], "sequence", index + 1);
        }
      });
    } else {
      // QUESTION DELETION: Remove question and recalculate sequences for remaining questions in same section

      // Remove the question
      updatedQuestions = _.filter(
        updatedQuestions,
        q => _.get(q, "id") !== questionId
      );

      // Get all questions in the same section as the deleted question
      const sectionQuestions = _.filter(
        updatedQuestions,
        q =>
          _.get(q, "parentIdV2") === deletingItemParentId &&
          _.get(q, "parentTypeV2") !== "ASSESSMENT"
      );

      // Sort by current sequence to maintain relative order
      const sortedQuestions = _.sortBy(sectionQuestions, "sequence");

      // Update sequences in the main array for questions in this section
      _.forEach(sortedQuestions, (question, index) => {
        const mainArrayIndex = _.findIndex(
          updatedQuestions,
          q => _.get(q, "id") === _.get(question, "id")
        );
        if (mainArrayIndex !== -1) {
          _.set(updatedQuestions[mainArrayIndex], "sequence", index + 1);
        }
      });
    }

    const updatedCacheData = update(cacheData, {
      questions: {
        edges: { $set: updatedQuestions },
      },
    });

    // Optimistically update cache
    writeAssessmentQuestionsInCacheV2(assessmentQuestionsQueryVariables, {
      node: updatedCacheData,
    });

    try {
      await client.mutate({
        mutation: deleteQuestionMutation,
        variables: {
          id: questionId,
          portalType: "PLANNER",
        },
      });
    } catch (e) {
      // If mutation fails, revert the cache to its previous state
      writeAssessmentQuestionsInCacheV2(assessmentQuestionsQueryVariables, {
        node: cacheData,
      });

      // Revert active question if it was changed
      if (activeQuestionId !== _.get(getState(), "worksheet.activeQuestion")) {
        dispatch(updateActiveQuestion(activeQuestionId));
      }

      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * @deprecated Use `duplicateQuestionInWorksheetV2` instead.
 * This function is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const duplicateQuestionInWorksheet = ({
  questionId,
  questionResourceId,
  parentResourceId,
  isSubQuestion,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const cacheData = getOrganizationResourceFromCache(
      parentResourceId,
      isSubQuestion
    );

    const questions = _.get(cacheData, "childrenV2.edges", []);
    const question = _.find(questions, question => {
      return _.get(question, "node.id") == questionResourceId;
    });

    // changing every children's id for multipart Question
    const newChildrenV2 = _.cloneDeep(
      _.get(question, ["node", "childrenV2", "edges"])
    );
    _.forEach(newChildrenV2, edge => {
      const newChildId = generateRandomId();
      const node = _.get(edge, "node", {});
      _.set(node, "id", newChildId);
      const item = _.get(node, "item", {});
      _.set(item, "id", newChildId);
      _.set(node, "item", item);
    });

    const questionHasChildren = _.has(_.get(question, "node"), "childrenV2");
    const newQuestionId = generateRandomId();
    const newQuestion = questionHasChildren
      ? update(question, {
          node: {
            id: { $set: newQuestionId },
            item: { id: { $set: newQuestionId } },
            childrenV2: {
              edges: { $set: newChildrenV2 },
              resourceTypeBasedCount: {
                totalCount: {
                  $set: _.size(newChildrenV2),
                },
              },
            },
          },
        })
      : update(question, {
          node: {
            id: { $set: newQuestionId },
            item: { id: { $set: newQuestionId } },
            edges: { $set: newChildrenV2 },
          },
        });

    const newQuestions = [];
    _.forEach(questions, question => {
      newQuestions.push(question);
      if (_.get(question, "node.id") == questionResourceId) {
        newQuestions.push(newQuestion);
      }
    });

    const updatedCacheData = update(cacheData, {
      childrenV2: {
        resourceTypeBasedCount: {
          totalCount: {
            $set:
              _.get(cacheData, "childrenV2.resourceTypeBasedCount.totalCount") +
              1,
          },
        },
        edges: { $set: newQuestions },
      },
    });

    writeOrganizationResourceInCache(parentId, updatedCacheData, isSubQuestion);
    dispatch(updateActiveQuestion(newQuestionId));

    try {
      const res = await client.mutate({
        mutation: duplicateQuestionInWorksheetMutation,
        variables: {
          input: {
            questionId: questionId,
            putAfterItemId: questionResourceId,
            parentResourceId: parentResourceId,
            parentId: parentId,
          },
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:question_successfully_duplicated",
          type: "success",
        })
      );
      // now fetching question resource
      const dupQuestion = _.get(res, [
        "data",
        "content",
        "duplicateQuestionInWorksheet",
        "questionResource",
      ]);

      const updatedQuestions = [];
      _.forEach(newQuestions, question => {
        if (_.get(question, "node.id") == newQuestionId) {
          updatedQuestions.push(
            update(newQuestion, {
              node: { $set: dupQuestion },
            })
          );
        } else {
          updatedQuestions.push(question);
        }
      });

      const updatedCacheData = update(cacheData, {
        childrenV2: {
          resourceTypeBasedCount: {
            totalCount: {
              $set:
                _.get(
                  cacheData,
                  "childrenV2.resourceTypeBasedCount.totalCount"
                ) + 1,
            },
          },
          edges: { $set: updatedQuestions },
        },
      });

      writeOrganizationResourceInCache(
        parentId,
        updatedCacheData,
        isSubQuestion
      );
      dispatch(updateActiveQuestion(_.get(dupQuestion, "item.id")));

      return questionId;
    } catch (e) {
      writeOrganizationResourceInCache(parentId, cacheData, isSubQuestion);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * Duplicates a question in the worksheet.
 *
 * @param {Object} params
 * @param {Array<string>} params.questionIds - Array of question IDs to duplicate
 * @param {string} params.parentItemId - Parent item ID (worksheet section/item)
 * @returns {Function} Redux thunk
 */
export const duplicateQuestionInWorksheetV2 = ({
  questionIds = [],
  parentItemId,
  putAfterItemId = null,
  isQueriesV2Enabled = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    if (_.isEmpty(questionIds) || _.isNil(parentItemId) || _.isNil(parentId))
      return;

    dispatch(updateLoadingState(true));
    const refetchQueries = [];

    if (isQueriesV2Enabled) {
      const assessmentQuestionsQueryVariables = {
        id: parentId,
        type: parentType,
        portalType: "PLANNER",
      };
      refetchQueries.push({
        query: getAssessmentQuestionsQueryV2,
        variables: assessmentQuestionsQueryVariables,
      });
    } else {
      //WORKSHEET_RESTRUCTURING: Remove this block when queriesV2 is enabled
      const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
        {
          parentId,
          parentType,
          portalType: "PLANNER",
        }
      );
      refetchQueries.push({
        query: getAssessmentQuestionsQuery,
        variables: assessmentQuestionsQueryVariables,
      });
    }

    try {
      const res = await client.mutate({
        mutation: duplicateQuestionInWorksheetV2Mutation,
        variables: {
          input: {
            questionIds,
            putAfterItemId,
            worksheetId: parentId,
            parentId: parentItemId,
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });

      dispatch(
        setToastMsg({
          msg: "toastMsgs:question_successfully_duplicated",
          type: "success",
        })
      );

      const dupQuestionIds = _.get(
        res,
        "data.content.duplicateQuestionInWorksheetV2.questionIds",
        []
      );

      dispatch(updateActiveQuestion(_.last(dupQuestionIds)));

      return dupQuestionIds;
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    } finally {
      dispatch(updateLoadingState(false));
    }
  };
};

/**
 * @deprecated Use `updateQuestionToCacheV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const updateQuestionToCache = ({
  newQuestion,
  enableQuestionSwitch = true,
  questionId,
  parentResourceId = null,
  questionResourceId = null,
  questionOffset = 0,
  isAddingQuestionsInBetween = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId,
        parentType,
        portalType: "PLANNER",
      }
    );

    const cacheData = getAssessmentQuestionsFromCache(
      assessmentQuestionsQueryVariables
    );

    const sections = _.get(
      cacheData,
      "organizationResourcesV2.edges[0].node.childrenV2.edges",
      []
    );
    let sectionIdx = _.size(sections) - 1;
    if (parentResourceId) {
      const index = _.findIndex(
        sections,
        section => _.get(section, "node.id") === parentResourceId
      );
      if (index !== -1) sectionIdx = index;
    }

    const questions = _.get(sections[sectionIdx], "node.childrenV2.edges", []);
    let questionIdx = isAddingQuestionsInBetween
      ? questionOffset
      : _.size(questions);
    if (questionResourceId) {
      const index = _.findIndex(
        questions,
        question => _.get(question, "node.id") === questionResourceId
      );
      if (index !== -1) questionIdx = index + questionOffset + 1;
    }

    const updatedCacheData = {
      node: update(cacheData, {
        organizationResourcesV2: {
          edges: {
            [0]: {
              node: {
                childrenV2: {
                  edges: {
                    [sectionIdx]: {
                      node: {
                        childrenV2: {
                          edges: {
                            $splice: [[questionIdx, 0, newQuestion]],
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      }),
    };
    writeAssessmentQuestionsInCache(
      assessmentQuestionsQueryVariables,
      updatedCacheData
    );
    if (enableQuestionSwitch && questionId) {
      dispatch(updateActiveQuestion(questionId));
    }
  };
};

export const updateSubmissionEvaluationScore = ({
  questionSubmissionId,
  studentId,
  newScoreValue,
  academicTermId,
  userFilter = "SELF",
}) => {
  return async () => {
    if (_.isNil(questionSubmissionId) || _.isNil(studentId)) return;
    const userInfo = getUserInfo();

    const shouldAttachTermIdWithRating = ACLStore.can(
      "FeatureFlag:AttachTermIdWithRating"
    );

    const scoreObjectDetails = {
      ibPYPElementType: "SCORE",
      value: newScoreValue,
    };

    let elementRating = {
      studentId: studentId,
      parentId: questionSubmissionId,
      parentType: "QUESTION_SUBMISSION",
      academicTermId: shouldAttachTermIdWithRating ? academicTermId : undefined,

      ...scoreObjectDetails,
      createdBy: userInfo.id,
    };

    const cacheData = getQuestionSubmissionFromCache({
      id: questionSubmissionId,
      userFilter,
    });

    try {
      const scoreObj = getScoreObjFromSubmission({
        ibPYPElementRatings: _.get(cacheData, "ibPYPElementRatings"),
      });
      const isScoreItemEmpty = _.isEmpty(scoreObj);
      const shouldReturnValue = isScoreItemEmpty;

      if (!shouldReturnValue) {
        elementRating = _.omit(elementRating, ["academicTermId"]);
      }
      if (!_.isEmpty(cacheData) && isScoreItemEmpty) {
        const newScoreObj = {
          academicCriteriaSet: null,
          academicCriteriaValue: null,
          ibPYPElementId: null,
          ibPYPElementType: "SCORE",
          id: generateRandomId(),
          value: newScoreValue,
          source: null,
          creatorType: "STAFF",
          isDeleted: false,
          evaluationJustification: null,
          __typename: "IBPYPElementRating",
        };

        const newIbPYPElementRatings = _.compact(
          _.concat([newScoreObj], _.get(cacheData, "ibPYPElementRatings", []))
        );

        const updatedCacheData = update(cacheData, {
          ibPYPElementRatings: {
            $set: newIbPYPElementRatings,
          },
        });

        writeQuestionSubmissionInCache({
          id: questionSubmissionId,
          data: updatedCacheData,
          userFilter,
        });
      }

      const response = await client.mutate({
        mutation: shouldReturnValue
          ? createIBPYPElementRatingsMutation
          : updateStudentAssessmentMarksMutation,
        variables: {
          elementRatings: [elementRating],
        },
      });

      return response;
    } catch (error) {
      if (!_.isEmpty(cacheData)) {
        writeQuestionSubmissionInCache({
          id: questionSubmissionId,
          data: cacheData,
          userFilter,
        });
      }
    }
  };
};

function isNumeric(value) {
  if (typeof value !== "string") {
    value = String(value);
  }
  const num = _.toNumber(value);
  return !_.isNaN(num) && value.trim() !== "";
}

/**
 * @deprecated use reorderQuestion instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const reorderOrganizationResource = ({
  resourceType,
  id,
  putAfterItemId,
  oldParentResourceId,
  newParentResourceId = null,
  fromIndex,
  toIndex,
}) => {
  return async (dispatch, getState) => {
    const switchParent =
      !_.isEqual(oldParentResourceId, newParentResourceId) &&
      !_.isNil(newParentResourceId);

    // Sentry for invalid newParentResourceId
    if (!isNumeric(newParentResourceId) && switchParent) {
      Sentry.captureMessage(
        "Invalid newParentResourceId in reorderOrganizationResource",
        {
          level: "error",
          tags: {
            component: "WorksheetModules",
          },
          extra: {
            functionName: "reorderOrganizationResource",
            newParentResourceId,
            oldParentResourceId,
            id,
            putAfterItemId,
            switchParent,
            resourceType,
          },
        }
      );
      return;
    }

    const parentResourceId = switchParent ? newParentResourceId : null;

    const state = getState();

    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId,
        parentType,
        portalType: "PLANNER",
      }
    );

    const cacheData = getAssessmentQuestionsFromCache(
      assessmentQuestionsQueryVariables
    );

    const variables = {
      id,
      putAfterItemId,
      switchParent,
      parentResourceId,
      portalType: "PLANNER",
    };

    const updatedCacheData = getReorderedWorksheetCacheData({
      cacheData,
      fromIndex,
      toIndex,
      oldParentResourceId,
      newParentResourceId,
      resourceType,
      switchParent,
    });

    writeAssessmentQuestionsInCache(assessmentQuestionsQueryVariables, {
      node: updatedCacheData,
    });

    try {
      await client.mutate({
        mutation: reorderOrganizationResourceMutation,
        variables,
      });
    } catch (e) {
      writeAssessmentQuestionsInCache(assessmentQuestionsQueryVariables, {
        node: cacheData,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * @deprecated use reorderQuestionV2 instead
 * Reorders a question in the worksheet by moving it to a new position
 */
export const reorderQuestion = ({
  questionId,
  putAfterQuestionId = null,
  fromIndex,
  toIndex,
  oldParentId,
  newParentId,
  resourceType,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    // Determine if we're moving the question to a different section
    const switchParent =
      !_.isEqual(oldParentId, newParentId) && !_.isNil(newParentId);
    const parentId = switchParent ? newParentId : oldParentId;

    const assessmentId = _.get(state, "worksheet.parentEntity.id", null);
    const assessmentType = _.get(state, "worksheet.parentEntity.type", null);

    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId: assessmentId,
        parentType: assessmentType,
        portalType: "PLANNER",
      }
    );

    // Get current data from cache
    const cacheData = getAssessmentQuestionsFromCache(
      assessmentQuestionsQueryVariables
    );

    const variables = {
      questionId,
      putAfterQuestionId,
      parentId,
      portalType: "PLANNER",
    };

    // Update cache with reordered data optimistically
    const updatedCacheData = getReorderedWorksheetCacheData({
      cacheData,
      fromIndex,
      toIndex,
      oldParentId,
      newParentId,
      resourceType,
      switchParent,
    });

    writeAssessmentQuestionsInCache(assessmentQuestionsQueryVariables, {
      node: updatedCacheData,
    });

    try {
      await client.mutate({
        mutation: reorderQuestionMutation,
        variables,
      });
    } catch (e) {
      // If mutation fails, revert the cache to its previous state
      writeAssessmentQuestionsInCache(assessmentQuestionsQueryVariables, {
        node: cacheData,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * Reorders a question or section in the worksheet using V2 flat data structure.
 * This function follows the logic from reorderOrganizationResource but adapts it
 * to work with flat question arrays instead of hierarchical cache data.
 *
 * Key features:
 * - Uses flat question structure for better performance
 * - Handles both question and section reordering
 * - Handles cross-section movement (switching parents) for questions
 * - Optimistic cache updates with error recovery
 * - Sequence-based positioning instead of array indices
 * - Validates parent IDs to prevent invalid operations
 */
export const reorderQuestionV2 = ({
  questionId,
  putAfterQuestionId = null,
  oldParentId,
  newParentId = null,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const assessmentId = _.get(state, "worksheet.parentEntity.id", null);
    const assessmentType = _.get(state, "worksheet.parentEntity.type", null);

    // Use V2 query variables structure (flat data)
    const assessmentQuestionsQueryVariables = {
      id: assessmentId,
      type: assessmentType,
      portalType: "PLANNER",
    };

    // Get current data from cache - assuming V2 structure with flat questions
    const cacheData = getAssessmentQuestionsFromCacheV2(
      assessmentQuestionsQueryVariables
    );

    // Work with flat questions array instead of hierarchical structure
    const flatQuestions = _.get(cacheData, "questions.edges", []);

    // Find the item being moved (could be question or section)
    const movingItem = _.find(flatQuestions, q => q.id === questionId);

    if (!movingItem) {
      console.error("reorderQuestionV2: Moving item not found", {
        questionId,
        availableItemIds: _.map(flatQuestions, "id"),
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return;
    }

    // Determine if the item being moved is a section
    const isSection = _.get(movingItem, "questionType.uid") === "section";

    // Determine if we're moving the question to a different section (only allowed for questions)
    const switchParent =
      !_.isEqual(oldParentId, newParentId) &&
      !_.isNil(newParentId) &&
      !isSection;

    // For sections, parentId should be the assessment ID (since sections belong to assessments)
    // For questions, parentId should be the target section ID
    const parentId = isSection
      ? _.get(movingItem, "parentIdV2")
      : switchParent
      ? newParentId
      : oldParentId;

    const variables = {
      questionId,
      putAfterQuestionId,
      parentId,
      portalType: "PLANNER",
    };

    // Create a copy of flatQuestions to modify
    const updatedQuestions = _.cloneDeep(flatQuestions);

    // Find the moving item in the updated array
    const updatedMovingItem = _.find(
      updatedQuestions,
      q => q.id === questionId
    );

    if (isSection) {
      // SECTION REORDERING: Only reorder among other sections at the root level

      // Get all sections (items with parentTypeV2 === "ASSESSMENT")
      const allSections = _.filter(
        updatedQuestions,
        q => q.questionType.uid === "section" && q.parentTypeV2 !== "ASSESSMENT"
      );

      // Sort by current sequence to maintain relative order
      let sortedSections = _.sortBy(allSections, "sequence");

      // Remove the moving section from its current position
      sortedSections = _.filter(sortedSections, q => q.id !== questionId);

      // Find insertion position
      let insertPosition = 0;
      if (putAfterQuestionId) {
        const afterSectionIndex = _.findIndex(
          sortedSections,
          q => q.id === putAfterQuestionId
        );
        insertPosition = afterSectionIndex + 1;
      }

      // Insert moving section at the correct position
      sortedSections.splice(insertPosition, 0, updatedMovingItem);

      // Update sequences in the main array for all sections
      _.forEach(sortedSections, (section, index) => {
        const mainArrayIndex = _.findIndex(
          updatedQuestions,
          q => q.id === section.id
        );
        if (mainArrayIndex !== -1) {
          updatedQuestions[mainArrayIndex].sequence = index + 1;
        }
      });
    } else if (switchParent) {
      // CROSS-SECTION MOVE: Change parent and recalculate all sequences for questions

      // Group all questions by their sections BEFORE updating the parent (excluding sections themselves)
      const questionsBySection = _.groupBy(
        _.filter(updatedQuestions, q => q.parentTypeV2 !== "ASSESSMENT"),
        "parentIdV2"
      );

      // Update the moving question's parent after grouping
      updatedMovingItem.parentIdV2 = parentId;

      // Recalculate sequences for each section
      _.forEach(questionsBySection, (sectionQuestions, sectionId) => {
        // Sort questions by their current sequence to maintain relative order
        const sortedQuestions = _.sortBy(sectionQuestions, "sequence");

        if (sectionId === parentId.toString()) {
          // This is the target section - need to insert our question at the right position
          let insertPosition = 0;

          if (putAfterQuestionId) {
            const afterQuestionIndex = _.findIndex(
              sortedQuestions,
              q => q.id === putAfterQuestionId
            );
            insertPosition = afterQuestionIndex + 1;
          }

          // Insert moving question at the correct position (no need to filter as it won't be in this section yet)
          sortedQuestions.splice(insertPosition, 0, updatedMovingItem);

          // Update sequences in the main array
          _.forEach(sortedQuestions, (question, index) => {
            const mainArrayIndex = _.findIndex(
              updatedQuestions,
              q => q.id === question.id
            );
            if (mainArrayIndex !== -1) {
              updatedQuestions[mainArrayIndex].sequence = index + 1;
            }
          });
        } else {
          // Other sections - remove the moving question and recalculate consecutive sequences
          const filteredQuestions = _.filter(
            sortedQuestions,
            q => q.id !== questionId
          );

          // Update sequences in the main array
          _.forEach(filteredQuestions, (question, index) => {
            const mainArrayIndex = _.findIndex(
              updatedQuestions,
              q => q.id === question.id
            );
            if (mainArrayIndex !== -1) {
              updatedQuestions[mainArrayIndex].sequence = index + 1;
            }
          });
        }
      });
    } else {
      // SAME-SECTION MOVE: Reorder within the same section (questions only)

      // Get all questions in this section
      const sectionQuestions = _.filter(
        updatedQuestions,
        q => q.parentIdV2 === oldParentId && q.parentTypeV2 !== "ASSESSMENT"
      );

      // Sort by current sequence to maintain relative order
      let sortedQuestions = _.sortBy(sectionQuestions, "sequence");

      // Remove the moving question from its current position
      sortedQuestions = _.filter(sortedQuestions, q => q.id !== questionId);

      // Find insertion position
      let insertPosition = 0;
      if (putAfterQuestionId) {
        const afterQuestionIndex = _.findIndex(
          sortedQuestions,
          q => q.id === putAfterQuestionId
        );
        insertPosition = afterQuestionIndex + 1;
      }

      // Insert moving question at the correct position
      sortedQuestions.splice(insertPosition, 0, updatedMovingItem);

      // Update sequences in the main array
      _.forEach(sortedQuestions, (question, index) => {
        const mainArrayIndex = _.findIndex(
          updatedQuestions,
          q => q.id === question.id
        );
        if (mainArrayIndex !== -1) {
          updatedQuestions[mainArrayIndex].sequence = index + 1;
        }
      });
    }

    const updatedCacheData = update(cacheData, {
      questions: {
        edges: { $set: updatedQuestions },
      },
    });

    // Optimistically update cache (like reorderOrganizationResource)
    writeAssessmentQuestionsInCacheV2(assessmentQuestionsQueryVariables, {
      node: updatedCacheData,
    });

    try {
      await client.mutate({
        mutation: reorderQuestionMutation,
        variables,
      });
    } catch (e) {
      const itemType = isSection ? "section" : "question";
      console.error(
        `reorderQuestionV2: ${itemType} reorder mutation failed, reverting cache`,
        {
          error: e.message,
          variables,
          itemType,
          isSection,
        }
      );
      // If mutation fails, revert the cache to its previous state (like reorderOrganizationResource)
      writeAssessmentQuestionsInCacheV2(assessmentQuestionsQueryVariables, {
        node: cacheData,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const createQuestionTopic = ({ label }) => {
  return async (dispatch, getState) => {
    const state = getState();

    const questionLibrarytopics = _.get(
      state,
      "worksheet.questionLibraryTags.topics",
      []
    );
    dispatch(
      updateQuestionLibraryTags({
        topics: update(questionLibrarytopics, {
          $push: [{ value: generateRandomId(), label }],
        }),
      })
    );
    const fetchVariables = { input: { types: [QUESTION_TOPIC] } };
    const topicsData = getQuestionTopicsFromCache(fetchVariables);

    try {
      const response = await client.mutate({
        mutation: createQuestionTopicMutation,
        variables: {
          input: {
            label,
            type: QUESTION_TOPIC,
          },
        },
      });

      let newTopic = {};
      const newTopicId = _.get(
        response,
        "data.content.createQuestionTag.id",
        null
      );

      if (newTopicId) {
        dispatch(
          updateQuestionLibraryTags({
            topics: update(questionLibrarytopics, {
              $push: [{ value: newTopicId, label }],
            }),
          })
        );
        newTopic = {
          id: newTopicId,
          label,
          type: QUESTION_TOPIC,
          __typename: "GenericTag",
        };
        const updatedData = update(topicsData, {
          content: {
            getQuestionTags: {
              $push: [newTopic],
            },
          },
        });
        writeQuestionTopicsInCache(fetchVariables, updatedData);
      }

      return newTopic;
    } catch (e) {
      writeQuestionTopicsInCache(fetchVariables, topicsData);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));

      return;
    }
  };
};

/**
 * @deprecated use addQuestionToSchoolLibraryV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const addQuestionToSchoolLibrary = ({ id }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const tagDetails = _.get(state, "worksheet.questionLibraryTags", {});
    const questionId = _.isNil(id)
      ? _.get(state, "worksheet.activeQuestion")
      : id;
    const curriculumProgramId = getSelectedCurriculumId(state);

    /** set added to library true in cache */
    updateQuestionMetadataInCache(questionId, {
      [METADATA_ADDED_TO_LIBRARY_KEY]: true,
    });

    const questionToLibraryMutationVariables = getAddQuestionToLibraryVariables(
      { tagDetails, questionId, curriculumProgramId }
    );
    try {
      const response = await client.mutate({
        mutation: addQuestionToSchoolLibraryMutation,
        variables: {
          input: questionToLibraryMutationVariables,
        },
      });
      const { isSuccess, warning } =
        _.get(response, "data.content.addQuestionToSchoolLibrary", {}) || {};

      if (isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:question_added_to_library_successfully",
            type: "success",
          })
        );
      } else {
        const message = _.get(warning, "message", "");
        throw new Error(message);
      }
    } catch (e) {
      updateQuestionMetadataInCache(questionId, {
        [METADATA_ADDED_TO_LIBRARY_KEY]: false,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * @deprecated Use `importQuestionFromLibraryV2` instead.
 * This function is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const importQuestionFromLibrary = ({
  questionIds,
  putAfterItemId = null,
  parentResourceId,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);
    const isToddleLibrary =
      state.login.activeTab === ACTIVE_TAB_TODDLE_LIBRARY ||
      _.get(state, "worksheet.questionLibrary.libraryType", []) ===
        TODDLE_LIBRARY;

    if (!_.isNil(parentId)) {
      dispatch(updateLoadingState(true));

      const variables = {
        questionIds,
        putAfterItemId,
        parentResourceId,
        parentId,
      };

      const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
        {
          parentId,
          parentType,
          portalType: "PLANNER",
        }
      );

      try {
        const mutation = isToddleLibrary
          ? importQuestionFromToddleLibraryMutation
          : importQuestionFromSchoolLibraryMutation;
        const mutationName = isToddleLibrary
          ? "importQuestionFromToddleLibrary"
          : "importQuestionFromSchoolLibrary";

        const res = await client.mutate({
          mutation,
          variables: {
            input: variables,
            portalType: "PLANNER",
          },
          refetchQueries: [
            {
              query: getAssessmentQuestionsQuery,
              variables: assessmentQuestionsQueryVariables,
            },
          ],
          awaitRefetchQueries: true,
        });

        const { isSuccess, warning = {} } =
          _.get(res, ["data", "content", mutationName], false) || {};

        if (isSuccess) {
          dispatch(
            setToastMsg({
              msg: "toastMsgs:questions_added_from_library_successfully",
              type: "success",
            })
          );
        } else {
          throw new Error(_.get(warning, "message", ""));
        }
      } catch (e) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      } finally {
        dispatch(updateLoadingState(false));
      }
    }
  };
};

/**
 * Imports questions from the library into the worksheet.
 * Handles both Toddle and School library imports, updates cache, and sets last question as active if required.
 *
 * @param {Object} params
 * @param {Array<string>} params.questionIds - Array of question IDs to import
 * @param {string|null} [params.putAfterItemId=null] - ID of the item after which to insert imported questions
 * @param {string} params.parentItemId - ID of the parent item (section/item)
 * @returns {Function} Redux thunk
 */
export const importQuestionFromLibraryV2 = ({
  questionIds,
  putAfterItemId = null,
  parentItemId,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);
    const isFromToddleLibraryPortal =
      _.get(state, "login.activeTab") === ACTIVE_TAB_TODDLE_LIBRARY;

    const isFromToddleLibrary =
      _.get(state, "worksheet.questionLibrary.libraryType") === TODDLE_LIBRARY;
    const isToddleLibrary = isFromToddleLibraryPortal || isFromToddleLibrary;

    const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

    if (_.isNil(parentItemId) || _.isNil(parentId)) return;

    dispatch(updateLoadingState(true));

    const variables = {
      questionIds,
      putAfterItemId,
      worksheetId: parentId,
      parentId: parentItemId,
    };
    const refetchQueries = [];
    let assessmentQuestionsQueryVariables = {};
    if (isQueriesV2Enabled) {
      assessmentQuestionsQueryVariables = {
        id: parentId,
        type: parentType,
        portalType: "PLANNER",
      };
      refetchQueries.push({
        query: getAssessmentQuestionsQueryV2,
        variables: assessmentQuestionsQueryVariables,
      });
    } else {
      //WORKSHEET_RESTRUCTURING: Remove else part once queriesV2 support is added in toddle library
      assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables({
        parentId,
        parentType,
        portalType: "PLANNER",
      });
      refetchQueries.push({
        query: getAssessmentQuestionsQuery,
        variables: assessmentQuestionsQueryVariables,
      });
    }

    try {
      const mutation = isToddleLibrary
        ? importQuestionFromToddleLibraryMutationV2
        : importQuestionFromSchoolLibraryMutationV2;
      const mutationName = isToddleLibrary
        ? "importQuestionFromToddleLibraryV2"
        : "importQuestionFromSchoolLibraryV2";

      const res = await client.mutate({
        mutation,
        variables: {
          input: variables,
          portalType: "PLANNER",
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });

      const { isSuccess, warning = {} } =
        _.get(res, ["data", "content", mutationName], false) || {};

      if (isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:questions_added_from_library_successfully",
            type: "success",
          })
        );
      } else {
        throw new Error(_.get(warning, "message", ""));
      }
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    } finally {
      dispatch(updateLoadingState(false));
    }
  };
};

/**
 * @deprecated Use `createWorksheetQuestionAI2` instead.
 * This function is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const createWorksheetQuestionAI = ({
  parentId,
  parentType,
  input,
  refetch = true,
}) => {
  return async () => {
    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId,
        parentType,
        portalType: "PLANNER",
      }
    );
    const refetchQueries = refetch
      ? [
          {
            query: getAssessmentQuestionsQuery,
            variables: assessmentQuestionsQueryVariables,
          },
        ]
      : [];

    try {
      const response = await client.mutate({
        mutation: createWorksheetQuestionsUsingAI,
        variables: {
          input,
          portalType: "PLANNER",
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      return response;
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const createWorksheetQuestionAIV2 = ({
  input,
  refetch = true,
  isQueriesV2Enabled = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    const refetchQueries = [];

    if (refetch) {
      if (isQueriesV2Enabled) {
        const assessmentQuestionsQueryVariables = {
          id: parentId,
          type: parentType,
          portalType: "PLANNER",
        };
        refetchQueries.push({
          query: getAssessmentQuestionsQueryV2,
          variables: assessmentQuestionsQueryVariables,
        });
      } else {
        //WORKSHEET_RESTRUCTURING: Remove this block when queriesV2 is enabled
        const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
          {
            parentId,
            parentType,
            portalType: "PLANNER",
          }
        );
        refetchQueries.push({
          query: getAssessmentQuestionsQuery,
          variables: assessmentQuestionsQueryVariables,
        });
      }
    }

    try {
      const response = await client.mutate({
        mutation: createWorksheetQuestionUsingAIV2,
        variables: {
          input,
          portalType: "PLANNER",
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      return response;
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

/**
 * @deprecated Use `replaceWorksheetQuestionAIV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const replaceWorksheetQuestionAI = ({
  parentId,
  parentType,
  input,
  questionIdToReplace,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
      {
        parentId,
        parentType,
        portalType: "PLANNER",
      }
    );

    try {
      const response = await client.mutate({
        mutation: replaceWorksheetQuestionUsingAI,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: getAssessmentQuestionsQuery,
            variables: assessmentQuestionsQueryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });

      const newQuestionId = _.get(
        response,
        "data.content.replaceWorksheetQuestionGeneratedFromAI.0.id"
      );

      const visibleQuestionAssistantIds = _.get(
        state,
        "worksheet.visibleQuestionAssistantIds",
        []
      );
      const newVisibleIds = _.filter(
        visibleQuestionAssistantIds,
        id => !_.isEqual(questionIdToReplace, id)
      );
      newVisibleIds.push(newQuestionId);
      dispatch(updateVisibleQuestionAssistantIds(newVisibleIds));

      dispatch(updateActiveQuestion(newQuestionId));

      return response;
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const replaceWorksheetQuestionAIV2 = ({
  input,
  questionIdToReplace,
  isQueriesV2Enabled = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    const refetchQueries = [];

    if (isQueriesV2Enabled) {
      const assessmentQuestionsQueryVariables = {
        id: parentId,
        type: parentType,
        portalType: "PLANNER",
      };
      refetchQueries.push({
        query: getAssessmentQuestionsQueryV2,
        variables: assessmentQuestionsQueryVariables,
      });
    } else {
      //WORKSHEET_RESTRUCTURING: Remove this block when queriesV2 is enabled
      const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
        {
          parentId,
          parentType,
          portalType: "PLANNER",
        }
      );
      refetchQueries.push({
        query: getAssessmentQuestionsQuery,
        variables: assessmentQuestionsQueryVariables,
      });
    }

    try {
      const response = await client.mutate({
        mutation: replaceWorksheetQuestionUsingAIV2Mutation,
        variables: {
          input,
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });

      const newQuestionId = _.get(
        response,
        "data.content.replaceWorksheetQuestionGeneratedFromAIV2.0.id"
      );

      const visibleQuestionAssistantIds = _.get(
        state,
        "worksheet.visibleQuestionAssistantIds",
        []
      );
      const newVisibleIds = [
        ..._.without(visibleQuestionAssistantIds, questionIdToReplace),
        newQuestionId,
      ];
      dispatch(updateVisibleQuestionAssistantIds(newVisibleIds));

      dispatch(updateActiveQuestion(newQuestionId));

      return response;
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const refetchGetStudentAssignmentDetails = ({
  assessmentQuestionsQueryVariables,
  getStudentAssignmentQuestionSubmissionsQueryVariables,
}) => {
  return async dispatch => {
    try {
      const assessmentQuestionsResponse = await client.query({
        query: getAssessmentQuestionsQuery,
        variables: assessmentQuestionsQueryVariables,
        fetchPolicy: "network-only",
      });
      const studentAssignmentSubmissionResponse = await client.query({
        query: getStudentAssignmentQuestionSubmissionsQuery,
        variables: getStudentAssignmentQuestionSubmissionsQueryVariables,
        fetchPolicy: "network-only",
      });

      return {
        assessmentQuestionsResponse,
        studentAssignmentSubmissionResponse,
      };
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const getStudentAssignmentQuestionSubmissions = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.query({
        query: getStudentAssignmentQuestionSubmissionsQuery,
        variables,
        fetchPolicy: "network-only",
      });
      return _.get(response, "data.node", {});
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const getSignedUrls = ({ urls }) => {
  return async dispatch => {
    try {
      const response = await client.query({
        query: getSignedUrlsQuery,
        variables: {
          input: {
            urls,
          },
        },
        fetchPolicy: "network-only",
      });
      return _.get(response, "data.content.getSignedUrl.signedUrls", {});
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const getSafeExamBrowserUrlHelper = variables => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: startSafeExamBrowserMutation,
        variables,
      });
      const data = _.get(
        response,
        "data.content.startSafeExamBrowserWorksheet.url",
        {}
      );
      const { isSuccess, warning = {} } =
        _.get(response, "data.content.startSafeExamBrowserWorksheet", false) ||
        {};

      if (!isSuccess) {
        throw new Error(_.get(warning, "message", ""));
      }
      return data;
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateQuestionOrder = async (
  assessmentId,
  questionSequenceMap
) => {
  try {
    const response = await client.mutate({
      mutation: updateQuestionOrderMutation,
      variables: {
        input: {
          assessmentId,
          questionSequenceMap,
        },
        portalType: "PLANNER",
      },
    });
    return response;
  } catch (e) {
    Sentry.captureMessage("Update question order mutation failed.", {
      extra: {
        level: "error",
        functionName: "updateQuestionOrderMutation",
        variables: { assessmentId, questionSequenceMap },
      },
    });
  }
};

/**
 * Creates and updates question fields in a worksheet.
 *
 * This function dispatches an update to a question with specified fields,
 * @param {Object} options - The options object.
 * @param {Array<string>} [options.uids=[]] - Array of unique identifiers for the fields.
 * @param {string} options.portalType - The type of portal being used.
 * @param {string} options.questionId - The ID of the question to update.
 * @returns {Function} A Redux thunk function that dispatches the update.
 */
export const createQuestionFieldV2 = ({
  uids = [],
  portalType,
  questionId,
}) => {
  return async (dispatch, getState) => {
    dispatch(updateLoadingState(true));
    const state = getState();

    const parentId = _.get(state, "worksheet.parentEntity.id", null);
    const parentType = _.get(state, "worksheet.parentEntity.type", null);

    if (!_.isNil(parentId) && !_.isNil(parentType)) {
      const assessmentQuestionsQueryVariables = {
        id: parentId,
        type: parentType,
        portalType,
      };

      const fields = _.map(uids, uid => ({ uid }));

      try {
        await dispatch(
          updateQuestion({
            id: questionId,
            fields: fields,
            refetchQueries: [
              {
                query: getAssessmentQuestionsQueryV2,
                variables: assessmentQuestionsQueryVariables,
              },
            ],
            awaitRefetchQueries: true,
          })
        );
      } catch {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      } finally {
        dispatch(updateLoadingState(false));
      }
    }
  };
};

/**
 * @returns {Function} A Redux thunk function that deletes question fields.
 */
export const deleteQuestionFieldV2 = ({
  ids = [],
  isAnswerField = false,
  questionId,
}) => {
  return async dispatch => {
    const cacheData = getQuestionFromCacheV2({
      id: questionId,
    });

    const fieldsKey = isAnswerField ? "answerFields" : "fields";

    const fields = _.get(cacheData, fieldsKey);
    const updatedFields = _.filter(
      fields,
      field => !_.includes(ids, _.get(field, "id"))
    );

    const updatedCacheData = update(cacheData, {
      [fieldsKey]: {
        $set: updatedFields,
      },
    });

    writeQuestionInCacheV2({
      id: questionId,
      data: updatedCacheData,
    });

    try {
      await dispatch(updateQuestion({ id: questionId, removedFields: ids }));
    } catch (e) {
      writeQuestionInCacheV2({
        id: questionId,
        data: cacheData,
      });
    }
  };
};

/**
 * @returns {Function} A Redux thunk function that adds a question to the school library.
 */
export const addQuestionToSchoolLibraryV2 = ({ id }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const tagDetails = _.get(state, "worksheet.questionLibraryTags", {});
    const questionId = _.isNil(id)
      ? _.get(state, "worksheet.activeQuestion")
      : id;
    const curriculumProgramId = getSelectedCurriculumId(state);

    /** set added to library true in cache */
    updateQuestionMetadataInCacheV2(questionId, {
      [METADATA_ADDED_TO_LIBRARY_KEY]: true,
    });

    const questionToLibraryMutationVariables = getAddQuestionToLibraryVariables(
      { tagDetails, questionId, curriculumProgramId }
    );
    try {
      const response = await client.mutate({
        mutation: addQuestionToSchoolLibraryMutation,
        variables: {
          input: questionToLibraryMutationVariables,
        },
      });
      const { isSuccess, warning } =
        _.get(response, "data.content.addQuestionToSchoolLibrary", {}) || {};

      if (isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:question_added_to_library_successfully",
            type: "success",
          })
        );
      } else {
        const message = _.get(warning, "message", "");
        throw new Error(message);
      }
    } catch (e) {
      updateQuestionMetadataInCacheV2(questionId, {
        [METADATA_ADDED_TO_LIBRARY_KEY]: false,
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * Adds multiple worksheet responses to the student portfolio as a single post.
 * This function handles the process of converting worksheet responses into portfolio posts
 * with all the relevant metadata and relationships.
 *
 * @param {Object} options - The options object
 * @param {Array<string>} options.assignmentUserMapIds - Array of assignment user map IDs to add to portfolio
 * @returns {Function} A Redux thunk function that adds the responses to portfolio
 */
export const addBulkWorksheetResponseToPortfolio = ({
  assignmentUserMapIds,
}) => {
  return async (dispatch, getState) => {
    try {
      // Get post details from Redux state
      const postDetails = getState().post.postDetails;
      const userInfo = getUserInfo();
      const userId = userInfo.id;

      const academicYearId = getSelectedAcademicYearId(getState());

      const {
        state = "DRAFT",
        assignmentId,
        title,
        unitPlan,
        resourceItem,
        resourceType,
        elementPYP,
        isPrivate,
        isHiddenFromStudents,
        folders,
        courseId,
        curriculumProgramId,
        originalIsStudentIndividuallyRated: isStudentIndividuallyRated,
        publishedAt,
        originalibPYPElementRatings: ibPYPElementRatings,
      } = postDetails;

      const resourceId = _.get(resourceItem, "id", null);

      const courseGradeFilters = {
        academicYearId,
      };

      // Get course details, first from cache or fetch if not available
      let courseDetails = getCourseDetailsFromCache({
        id: courseId,
        courseGradeFilters,
      });
      if (_.isEmpty(courseDetails) && !!courseId) {
        courseDetails = await dispatch(
          getCourseDetails({ id: courseId, courseGradeFilters })
        );
      }

      // Extract academic years from course details
      const academicYears = _.map(
        _.get(courseDetails, "academicYears", []),
        item => item.id
      );

      // Process folders - separate new folders from existing ones
      const createFolders = _.map(
        _.filter(folders, folder => _.get(folder, "isNewTag", false) == true),
        folder => ({
          name: _.get(folder, "name", ""),
          color: _.get(folder, "color", ""),
        })
      );
      const existingFolders = _.filter(
        folders,
        folder => _.get(folder, "isNewTag", false) == false
      );
      const folderNames = _.map(
        _.filter(folders, folder => _.get(folder, "isNewTag", false) == true),
        folder => _.get(folder, "name", "")
      );

      // Process PYP element ratings (learning goals/criteria)
      const selectedGoalsRatings = [];
      _.forEach(elementPYP, element => {
        selectedGoalsRatings.push(
          ...getNestedTaggedLearningGoalsIds(
            element,
            _.get(element, "value", [])
          )
        );
      });

      // Prepare element ratings input for the mutation
      const ibPYPElementRatingsInput = [];
      _.forEach(ibPYPElementRatings, rating => {
        // Only include ratings that match selected goals or are DP subjects
        // and have a valid student ID
        if (
          (_.some(
            selectedGoalsRatings,
            selectedRating =>
              selectedRating ==
              `${_.get(rating, "ibPYPElementType", undefined)}_${_.get(
                rating,
                "ibPYPElementId",
                undefined
              )}`
          ) ||
            rating?.ibPYPElementType === "DP_SUBJECT") &&
          !_.isUndefined(rating?.studentId)
        ) {
          const studentRating = {
            studentId: _.get(
              rating,
              "studentId",
              _.get(rating, "student.id", undefined)
            ),
            parentType: "POST",
            academicCriteriaValueId: _.get(
              rating,
              "academicCriteriaValue.id",
              undefined
            ),
            academicCriteriaSetId: _.get(
              rating,
              "academicCriteriaSet.id",
              undefined
            ),
            // Include learning course ID only if course-based grading is enabled
            learningCourseId: getIsCourseBasedGradingEnabled()
              ? _.get(
                  rating,
                  "learningCourseId",
                  getSelectedLearningCourseId(getState())
                )
              : undefined,
            ibPYPElementId: _.get(rating, "ibPYPElementId", undefined),
            ibPYPElementType: _.get(rating, "ibPYPElementType", ""),
            createdBy: userInfo.id,
            // Include subject ID only if subject-based grading is enabled
            subjectId:
              !getIsCourseBasedGradingEnabled() ||
              getIsSubjectBasedGradingEnabled()
                ? _.get(rating, "subjectId", undefined)
                : undefined,
            academicTermId: _.get(postDetails, "academicTerm.id", null),
          };
          ibPYPElementRatingsInput.push(studentRating);
        }
      });

      // Build the complete post input object for the mutation
      const postInput = {
        feedType: "PORTFOLIO",
        courseId,
        title,
        attachments: [],
        studentIds: [],
        unitPlanId: _.get(unitPlan, "id", null),
        resourceId,
        createdBy: userId,
        resourceType,
        elementsPYP: _.map(elementPYP, element => ({
          fieldUID: _.get(element, "fieldUID"),
          parentType: _.get(element, "parentType", "RESOURCE"),
          value: _.get(element, "value", []),
        })),
        taggedEntity: resourceId
          ? [
              {
                entityId: resourceId,
                entityType: resourceType,
              },
            ]
          : [],
        state,
        isPrivate,
        isHiddenFromStudents,
        publishedBy: userId,
        publishedAt,
        folders: _.map(existingFolders, folder => folder.id),
        ...(!isPortfolioParityEnabled() ? { folderNames } : {}), //removing this as support for this is added in createFolders.,
        curriculumProgramId,
        academicYears,
        ibPYPElementRatings: _.map(ibPYPElementRatingsInput, rating => ({
          ...rating,
          isPublished: true,
        })),
        isStudentIndividuallyRated,
        academicTermId: _.get(postDetails, "academicTerm.id", null),
        createFolders,
      };

      // Execute the mutation
      await client.mutate({
        mutation: addBulkStudentResponseToPortfolioMutation,
        variables: {
          input: {
            assignmentUserMapIds,
            postInput,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAssignmentDetailsQuery,
            variables: getAssignmentDetailsQueryVariables({
              assignmentId,
              curriculumProgramType: getCurrentCurriculumProgramType(
                getState()
              ),
              userType: userInfo.user_type,
              userId,
              academicYearId,
            }),
          },
        ],
      });
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

/**
 * Creates a new question in the worksheet
 */
export const createQuestionForToddleLibraryV2 = ({
  parentId,
  parentType,
  questionTypeId,
  fields,
  putAfterQuestionId = null,
  isQueriesV2Enabled = false,
  setQuestionAsActive = true,
  assessmentId,
  assessmentType,
}) => {
  return async dispatch => {
    // Ensure all required IDs are available
    if (
      !_.isNil(assessmentId) &&
      !_.isNil(assessmentType) &&
      !_.isNil(parentId) &&
      !_.isNil(parentType)
    ) {
      dispatch(updateLoadingState(true));

      const variables = {
        input: {
          parentId,
          parentType,
          questionTypeId,
          putAfterQuestionId,
          fields,
        },
        portalType: "PLANNER",
      };
      const refetchQueries = [];

      if (isQueriesV2Enabled) {
        // Prepare query variables for refetching assessment questions after mutation
        const assessmentQuestionsQueryVariables = {
          id: assessmentId,
          type: assessmentType,
          portalType: "PLANNER",
        };

        refetchQueries.push({
          query: getAssessmentQuestionsQueryV2,
          variables: assessmentQuestionsQueryVariables,
        });
      } else {
        //WORKSHEET_RESTRUCTURING: Remove this block when queriesV2 is enabled
        // Prepare query variables for refetching assessment questions after mutation
        const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
          {
            parentId: assessmentId,
            parentType: assessmentType,
            portalType: "PLANNER",
          }
        );
        refetchQueries.push({
          query: getAssessmentQuestionsQuery,
          variables: assessmentQuestionsQueryVariables,
        });
      }

      try {
        // Execute the mutation to create the question
        const res = await client.mutate({
          mutation: createQuestionMutationV2,
          variables,
          refetchQueries,
          awaitRefetchQueries: true,
        });

        const newQuestionId = _.get(
          res,
          "data.content.createQuestionV2.id",
          ""
        );

        // Set the active question to the newly created one
        if (setQuestionAsActive) {
          dispatch(updateActiveQuestion(newQuestionId));
        }

        return newQuestionId;
      } catch (e) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      } finally {
        dispatch(updateLoadingState(false));
      }
    }
  };
};

/**
 * Downloads bulk student responses for worksheets
 * Uses downloadBulkStudentResponse mutation for worksheet downloads
 */
export const bulkDownloadWorksheetResponse = ({
  assignmentUserMapIds,
  assignmentId,
}) => {
  return async dispatch => {
    try {
      const userInfo = getUserInfo();

      const response = await client.mutate({
        mutation: downloadBulkStudentResponseMutation,
        variables: {
          input: {
            assignmentUserMapIds,
            assignmentId,
          },
        },
      });

      const isSuccess = _.get(
        response,
        "data.content.downloadBulkStudentResponse.isSuccess",
        false
      );

      if (isSuccess) {
        dispatch(
          setToastMsg({
            type: "info",
            msg: "worksheet:bulk_download_submissions_message",
            locale_params: [
              {
                key: "email",
                value: userInfo.email,
                isPlainText: true,
              },
            ],
            position: "toast-bottom-left",
            timeOut: 4000,
          })
        );
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const registerOfflineTimeTrackingEvents = ({ events }) => {
  return async () => {
    try {
      let eventsToSend = events;
      if (getIsStudent()) {
        const allowedEventTypes = ["START", "RESUME", "PAUSE", "END"];
        eventsToSend = _.filter(events, ({ eventType }) =>
          _.includes(allowedEventTypes, eventType)
        );
      }

      if (_.isEmpty(eventsToSend)) {
        removeTimeTrackingEventsFromLocalStorage();
        return;
      }

      await client.mutate({
        mutation: createEntityEventTrackingMutation,
        variables: { input: eventsToSend },
      });
      removeTimeTrackingEventsFromLocalStorage();
    } catch (e) {}
  };
};

export const createEntityEventTracking = ({
  entityId,
  entityType,
  eventTime,
  eventType: _eventType,
  duration = null,
  reason = null,
  debugMetadata = {},
  queryParams,
  callbackOnError,
  metadata = {},
  isWorksheetWebView = false,
}) => {
  return async (dispatch, getState) => {
    const isStudentAssignmentRevampEnabled = getIsStudentAssignmentRevampEnabled();
    const getStudentAssignmentDetails = isWorksheetWebView
      ? getStudentAssignmentFromCacheV2
      : isStudentAssignmentRevampEnabled
      ? getStudentAssignmentFromCache
      : getStudentAssignmentDetailsFromCache;
    const writeStudentAssignmentDetails = isWorksheetWebView
      ? writeStudentAssignmentInCacheV2
      : isStudentAssignmentRevampEnabled
      ? writeStudentAssignmentInCache
      : writeStudentAssignmentDetailsInCache;

    const academicYearId = getSelectedAcademicYearId(getState());
    const userArchivedFilters = !isWorksheetWebView ? { academicYearId } : {};
    const oldStudentAssignmentDetails = getStudentAssignmentDetails(
      queryParams
    );

    let eventType = _eventType;
    const sortedCurrentEvents = _.orderBy(
      _.get(oldStudentAssignmentDetails, "events", []),
      ["eventTime"],
      ["desc"]
    );
    const lastStartEndOrReopenEvent = _.find(
      sortedCurrentEvents,
      ({ eventType }) =>
        eventType === "START" || eventType === "END" || eventType === "REOPEN"
    );
    const isTestStarted = lastStartEndOrReopenEvent?.eventType === "START";
    const isTestEnded = lastStartEndOrReopenEvent?.eventType === "END";
    const isTestReopened = lastStartEndOrReopenEvent?.eventType === "REOPEN";

    //CASE 1: If the current event is START but a test is already started, change it to RESUME
    if (_eventType === "START" && isTestStarted) eventType = "RESUME";

    //CASE 2: If the test is already ended and test is not reopened, ignore any further END events
    if (isTestEnded && !isTestReopened && eventType === "END") return;

    // Prepare current event input to be sent
    let currentEventInput = {
      entityId,
      entityType,
      eventTime,
      eventType,
      debugMetadata,
    };
    if (!_.isEmpty(metadata)) {
      _.set(currentEventInput, "metadata", metadata);
    }
    if (eventType === "RESUME") {
      currentEventInput = { ...currentEventInput, duration, reason };
    }
    let eventsToSend = [currentEventInput];

    // Prepare optimistic response
    let mutationResponse = [
      {
        id: generateRandomId(),
        eventType,
        eventTime,
        duration,
        reason,
        __typename: "EntityEventTrack",
      },
    ];

    // Fetch any offline events from local storage (except when current event is PAUSE)
    const eventsFromLocalStorage =
      eventType !== "PAUSE"
        ? getTimeTrackingEventsFromLocalStorage(entityId)
        : [];

    // If there are offline events, merge them with current event
    if (!_.isEmpty(eventsFromLocalStorage)) {
      const filteredLocalEvents = _.filter(
        eventsFromLocalStorage,
        ({ eventTime: currentEventTime }) =>
          !checkIsDateSame({
            date1: currentEventTime,
            date2: eventTime,
            check: "seconds",
          })
      );

      // Combine local events with current event and sort by eventTime
      // Keep only the last event in each consecutive run of the same eventType
      const allEvents = [...filteredLocalEvents, ...eventsToSend];
      const sortedEvents = _.orderBy(allEvents, ["eventTime"], ["asc"]);
      const uniqueEvents = [];

      _.forEach(sortedEvents, (event, index) => {
        const nextEvent = sortedEvents[index + 1] || null;
        const nextEventType = nextEvent?.eventType;
        const currEventType = event?.eventType;

        // If next event exists and has same eventType, skip current
        if (nextEventType === currEventType) {
          return;
        }

        // Otherwise push this event
        uniqueEvents.push(event);
      });

      eventsToSend = uniqueEvents;

      mutationResponse = [
        ..._.map(
          filteredLocalEvents,
          ({ eventType, eventTime, duration = null, reason = null }) => {
            return {
              id: generateRandomId(),
              eventType,
              eventTime,
              duration,
              reason,
              __typename: "EntityEventTrack",
            };
          }
        ),
        ...mutationResponse,
      ];
    }

    if (getIsStudent()) {
      const allowedEventTypes = ["START", "RESUME", "PAUSE", "END"];
      eventsToSend = _.filter(eventsToSend, ({ eventType }) =>
        _.includes(allowedEventTypes, eventType)
      );
      mutationResponse = _.filter(mutationResponse, ({ eventType }) =>
        _.includes(allowedEventTypes, eventType)
      );
    }

    if (_.isEmpty(eventsToSend)) return;

    const refetchQueries = [];
    if (eventType === "END" && !isWorksheetWebView) {
      refetchQueries.push(
        isStudentAssignmentRevampEnabled
          ? { query: getStudentAssignmentQuery, variables: queryParams }
          : {
              query: getStudentAssignmentDetailsQuery,
              variables: queryParams,
            }
      );
    }

    try {
      const response = await client.mutate({
        mutation: createEntityEventTrackingMutation,
        variables: { input: eventsToSend },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            createEntityEventTracking: {
              response: mutationResponse,
              warning: null,
              __typename: "GenericCreateEntityEventTrackingResponse",
            },
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
        update: (
          cache,
          {
            data: {
              platform: {
                createEntityEventTracking: { response },
              },
            },
          }
        ) => {
          if (eventType !== "END") {
            const oldEvents = _.get(oldStudentAssignmentDetails, "events", []);
            const newEvents = [...oldEvents, ...response];

            setTimeout(() => {
              const studentAssignmentDetails = getStudentAssignmentDetails(
                queryParams
              );
              const newStudentAssignmentDetails = {
                ...studentAssignmentDetails,
                events: newEvents,
              };
              writeStudentAssignmentDetails({
                ...queryParams,
                data: newStudentAssignmentDetails,
                userArchivedFilters,
              });
            });
          }
        },
      });

      if (eventType === "PAUSE" || !_.isEmpty(eventsFromLocalStorage)) {
        removeTimeTrackingEventsFromLocalStorage(entityId);
      }

      if (eventType === "END") {
        // * for `END` eventType, marking isSubmitted as `True` for syncing UI on submit action
        const updatedStudentAssignmentDetails = getStudentAssignmentDetails(
          queryParams
        );
        if (_.isObject(updatedStudentAssignmentDetails)) {
          writeStudentAssignmentDetails({
            ...queryParams,
            data: {
              ...updatedStudentAssignmentDetails,
              isSubmitted: true,
            },
            userArchivedFilters,
          });
        }
      }

      return { ...response, successful: true };
    } catch (e) {
      callbackOnError?.();

      if (eventType !== "END") {
        setTimeout(() => {
          writeStudentAssignmentDetails({
            ...queryParams,
            data: oldStudentAssignmentDetails,
            userArchivedFilters,
          });
        });
      }
      if (e.networkError) {
        return {
          successful: false,
          errors: ["toastMsgs:no_internet_connection"],
        };
      } else {
        return {
          successful: false,
          errors: ["toastMsgs:something_went_wrong"],
        };
      }
    }
  };
};

/** REDUX */
export const NAME = "worksheet";
const UPDATE_ACTIVE_QUESTION = `UPDATE_ACTIVE_QUESTION ${NAME}`;
const UPDATE_ACTIVE_QUESTION_FIELD = `UPDATE_ACTIVE_QUESTION_FIELD ${NAME}`;
const UPDATE_PARENT_ENTITY = `UPDATE_PARENT_ENTITY ${NAME}`;
const UPDATE_LOADING_STATE = `UPDATE_LOADING_STATE ${NAME}`;
const RESET_REDUX_STATE = `RESET_REDUX_STATE ${NAME}`;
const UPDATE_QUESTION_TAG = `UPDATE_QUESTION_TAG ${NAME}`;
const DISABLE_SCROLL_EVENT = `DISABLE_SCROLL_EVENT ${NAME}`;
const UPDATE_ERROR_STATE = `UPDATE_ERROR_STATE ${NAME}`;
const SET_ERROR_STATE = `SET_ERROR_STATE ${NAME}`;
const UPDATE_SELECTED_LIBRARY_QUESTIONS = `UPDATE_SELECTED_LIBRARY_QUESTIONS ${NAME}`;
const UPDATE_QUESTION_LIBRARY_FILTERS = `UPDATE_QUESTION_LIBRARY_FILTERS ${NAME}`;
const RESET_QUESTION_LIBRARY_STATE = `RESET_QUESTION_LIBRARY_STATE ${NAME}`;
const UPDATE_QUESTION_LIBRARY_TYPE = `UPDATE_QUESTION_LIBRARY_TYPE ${NAME}`;
const AI_ENHANCE_QUESTION = `AI_ENHANCE_QUESTION ${NAME}`;
const AI_ENHANCE_PROMPTS = `AI_ENHANCE_PROMPTS ${NAME}`;
const UPDATE_GRAPH_THEATER_MODE = `UPDATE_GRAPH_THEATER_MODE ${NAME}`;
const UPDATE_PARENT_SUBMISSION_ID = `UPDATE_PARENT_SUBMISSION_ID ${NAME}`;
const UPDATE_AI_QUESTION_GENERATION = `UPDATE_AI_QUESTION_GENERATION ${NAME}`;
const UPDATE_GENERATING_AI_QUESTIONS = `UPDATE_GENERATING_AI_QUESTIONS ${NAME}`;
const UPDATE_VISIBLE_QUESTION_ASSISTANT_IDS = `UPDATE_VISIBLE_QUESTION_ASSISTANT_IDS ${NAME}`;
const UPDATE_QUESTION_THEATER_MODE = `UPDATE_QUESTION_THEATER_MODE ${NAME}`;
const UPDATE_ENABLE_AUTO_SUBMIT = `UPDATE_ENABLE_AUTO_SUBMIT ${NAME}`;
const UPDATE_TEACHER_PREVIEW_MODE = `UPDATE_TEACHER_PREVIEW_MODE ${NAME}`;
const UPDATE_EXTRACT_FROM_DOC = `UPDATE_EXTRACT_FROM_DOC ${NAME}`;
const UPDATE_WORKSHEET_SIDEBAR = `UPDATE_WORKSHEET_SIDEBAR ${NAME}`;
const UPDATE_AI_ASSESSMENT_TOOLS = `UPDATE_AI_ASSESSMENT_TOOLS ${NAME}`;
const UPDATE_ADD_QUESTIONS_IN_BETWEEN = `UPDATE_ADD_QUESTIONS_IN_BETWEEN ${NAME}`;
const UPDATE_AI_PANEL = `UPDATE_AI_PANEL ${NAME}`;
const UPDATE_WORKSHEET_SUBMISSION_MODAL = `UPDATE_WORKSHEET_SUBMISSION_MODAL ${NAME}`;

export const updateIsTeacherPreviewModeEnabled = payload => {
  return { type: UPDATE_TEACHER_PREVIEW_MODE, payload };
};

export const updateActiveQuestion = payload => {
  return { type: UPDATE_ACTIVE_QUESTION, payload };
};

export const updateActiveQuestionField = payload => {
  return { type: UPDATE_ACTIVE_QUESTION_FIELD, payload };
};

export const updateParentEntity = payload => {
  return { type: UPDATE_PARENT_ENTITY, payload };
};

export const updateLoadingState = payload => {
  return { type: UPDATE_LOADING_STATE, payload };
};

export const setErrorState = payload => {
  return { type: SET_ERROR_STATE, payload };
};

export const updateErrorState = payload => {
  return { type: UPDATE_ERROR_STATE, payload };
};

export const disableScrollEvent = payload => {
  return { type: DISABLE_SCROLL_EVENT, payload };
};

export const resetState = () => {
  return { type: RESET_REDUX_STATE };
};

export const updateSelectedLibraryQuestions = payload => {
  return { type: UPDATE_SELECTED_LIBRARY_QUESTIONS, payload };
};

export const updateQuestionLibraryFilters = payload => {
  return {
    type: UPDATE_QUESTION_LIBRARY_FILTERS,
    payload,
    filterGroup: "filters",
  };
};

export const updateQuestionLibraryDefaultFilters = payload => {
  return {
    type: UPDATE_QUESTION_LIBRARY_FILTERS,
    payload,
    filterGroup: "defaultFilters",
  };
};

export const resetQuestionLibraryState = payload => {
  return { type: RESET_QUESTION_LIBRARY_STATE, payload };
};

export const updateQuestionLibraryTags = payload => {
  return { type: UPDATE_QUESTION_TAG, payload };
};
export const updateQuestionLibraryType = payload => {
  return { type: UPDATE_QUESTION_LIBRARY_TYPE, payload };
};

export const updateAiEnhanceQuestion = payload => {
  return { type: AI_ENHANCE_QUESTION, payload };
};

export const setAiEnhancePrompts = payload => {
  return { type: AI_ENHANCE_PROMPTS, payload };
};

export const updateIsGraphTheaterModeEnabled = payload => {
  return { type: UPDATE_GRAPH_THEATER_MODE, payload };
};

export const updateParentSubmissionId = payload => {
  return { type: UPDATE_PARENT_SUBMISSION_ID, payload };
};

export const updateIsAIQuestionGenerationEnabled = payload => {
  return { type: UPDATE_AI_QUESTION_GENERATION, payload };
};
export const updateGeneratingAIQuestions = payload => {
  return { type: UPDATE_GENERATING_AI_QUESTIONS, payload };
};

export const updateVisibleQuestionAssistantIds = payload => {
  return { type: UPDATE_VISIBLE_QUESTION_ASSISTANT_IDS, payload };
};

export const updateIsQuestionTheaterModeEnable = payload => {
  return { type: UPDATE_QUESTION_THEATER_MODE, payload };
};

export const updateEnableAutoSubmit = payload => {
  return { type: UPDATE_ENABLE_AUTO_SUBMIT, payload };
};

export const updateExtractFromDoc = payload => {
  return { type: UPDATE_EXTRACT_FROM_DOC, payload };
};

export const updateWorksheetSidebar = payload => {
  return { type: UPDATE_WORKSHEET_SIDEBAR, payload };
};

export const updateAIAssessmentTools = payload => {
  return { type: UPDATE_AI_ASSESSMENT_TOOLS, payload };
};

export const updateAddQuestionsInBetween = payload => {
  return { type: UPDATE_ADD_QUESTIONS_IN_BETWEEN, payload };
};

export const updateWorksheetAIPanel = payload => {
  return { type: UPDATE_AI_PANEL, payload };
};

export const updateWorksheetSubmissionModal = payload => {
  return { type: UPDATE_WORKSHEET_SUBMISSION_MODAL, payload };
};

const initialState = {
  activeQuestion: null,
  activeQuestionField: null,
  parentEntity: {
    id: null,
    type: null,
    submissionId: null,
  },
  isLoading: false,
  questionLibraryTags: {
    grades: [],
    subjects: [],
    topics: [],
    difficulty: [],
  },
  isScrollEventDisabled: false,
  errorState: {} /** tracks which questions have error state toggled: {[questionId1]: true, [questionId2]: false} */,
  questionLibrary: {
    defaultFilters: {
      selectedGrades: [],
      selectedSubjects: [],
    },
    filters: {
      searchText: "",
      selectedGrades: [],
      selectedSubjects: [],
      selectedStaff: [],
      selectedDifficulty: [],
      selectedQuestionTypes: [],
      selectedTopics: [],
      orderBy: "CREATED_AT",
      questionFeedType: QUESTION_FEED_TYPE_ALL_QUESTIONS,
      selectedSubTopics: [],
      selectedPaperTypes: [],
    },
    selectedQuestions: [],
    libraryType: SCHOOL_LIBRARY,
  },
  aiEnhanceQuestion: {
    action: null,
    questionText: null,
    hasGeneratedQuestion: true,
  },
  aiEnhancePrompts: [],
  isGraphTheaterModeEnabled: false,
  parentSubmissionId: null,
  isAIQuestionGenerationEnabled: false,
  generatingAIQuestions: false,
  visibleQuestionAssistantIds: [],
  isQuestionTheaterModeEnable: false,
  enableAutoSubmit: false,
  isTeacherPreviewModeEnabled: null,
  extractFromDoc: {},
  sidebar: {
    isOpen: true,
    defaultActive: null,
    showErrors: false,
  },
  aiAssessmentTools: {
    isOpen: false,
    preStepDebugInfoMetaData: {},
    debugInfoMetaData: {},
  },
  addQuestionsInBetween: {
    isAddingQuestionsInBetween: false,
    isAddingAtStartOfMultipart: false,
    questionResourceId: null,
    parentResourceId: null,
    nextQuestionResourceId: null,
    questionId: null,
    parentItemId: null,
    nextQuestionId: null,
  },
  aiPanel: {
    isOpen: true,
    conversationId: null,
  },
  showWorksheetSubmissionModal: false,
};

/** AI Conversation Attachment Helpers */

const TEMP_ID = "TEMP_ID";

export const pushNewAttachmentToCache = params => {
  const { aiConversationId, attachments } = params;

  const conversationData = getAIConversationsFromCache({
    id: aiConversationId,
  });

  let updatedConversationData = conversationData;

  const conversationAttachments = _.get(conversationData, "attachments", []);

  if (_.some(conversationAttachments, ({ id }) => _.includes(id, TEMP_ID))) {
    const filteredAttachments = _.filter(
      conversationAttachments,
      ({ id }) => !_.includes(id, TEMP_ID)
    );

    const newAttachments = [...filteredAttachments, ...attachments];

    updatedConversationData = update(conversationData, {
      attachments: {
        $set: newAttachments,
      },
    });
  } else {
    updatedConversationData = update(conversationData, {
      attachments: {
        $push: attachments,
      },
    });
  }

  writeAIConversationsInCache({
    variables: {
      id: aiConversationId,
    },
    data: {
      node: updatedConversationData,
    },
  });
};

export const deleteAIConversationAttachment = ({
  input,
  aiConversationId,
  portalType,
}) => {
  return async (dispatch, getState) => {
    const conversationData = getAIConversationsFromCache({
      id: aiConversationId,
    });

    try {
      const { attachmentIds } = input || {};

      const updatedConversationData = {
        ...(conversationData || {}),
        attachments: _.filter(
          conversationData?.attachments,
          attachment => !_.includes(attachmentIds, _.get(attachment, "id"))
        ),
        __typename: "AiConversation",
      };

      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: updatedConversationData,
        },
      });

      return await client.mutate({
        mutation: deleteAttachmentMutation,
        variables: { attachmentIds },
        ...(portalType === "PLANNER"
          ? { context: { portalType: "PLANNER" } }
          : {}),
      });
    } catch (err) {
      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: conversationData,
        },
      });
    }
  };
};

const REDUCER_HANDLERS = {
  [UPDATE_TEACHER_PREVIEW_MODE]: (state, action) =>
    update(state, {
      isTeacherPreviewModeEnabled: { $set: action.payload },
    }),
  [UPDATE_ACTIVE_QUESTION]: (state, action) =>
    update(state, {
      activeQuestion: { $set: action.payload },
    }),
  [UPDATE_ACTIVE_QUESTION_FIELD]: (state, action) =>
    update(state, {
      activeQuestionField: { $set: action.payload },
    }),
  [UPDATE_PARENT_ENTITY]: (state, action) => {
    return update(state, {
      parentEntity: { $set: action.payload },
    });
  },
  [UPDATE_LOADING_STATE]: (state, action) =>
    update(state, { isLoading: { $set: action.payload } }),
  [SET_ERROR_STATE]: (state, action) =>
    update(state, { errorState: { $set: action.payload } }),
  [UPDATE_ERROR_STATE]: (state, action) => {
    const { questionId = null, value = false } =
      _.get(action, "payload", {}) || {};

    return update(state, {
      errorState: {
        [questionId]: { $set: value },
      },
    });
  },
  [UPDATE_QUESTION_LIBRARY_FILTERS]: (state, action) => {
    const payload = _.get(action, "payload", {});
    const filterGroup = _.get(action, "filterGroup", "filters");
    const resolvedPayload = _.isArray(payload) ? payload : [payload];

    _.forEach(resolvedPayload, filter => {
      const { key, value } = filter;

      state = update(state, {
        questionLibrary: {
          [filterGroup]: { [key]: { $set: value } },
        },
      });
    });

    return state;
  },
  [UPDATE_SELECTED_LIBRARY_QUESTIONS]: (state, action) => {
    return update(state, {
      questionLibrary: {
        selectedQuestions: { $set: action.payload },
      },
    });
  },
  [RESET_QUESTION_LIBRARY_STATE]: state => {
    return update(state, {
      // do not reset default filters
      questionLibrary: {
        filters: { $set: initialState.questionLibrary.filters },
        selectedQuestions: {
          $set: initialState.questionLibrary.selectedQuestions,
        },
      },
    });
  },
  [UPDATE_QUESTION_LIBRARY_TYPE]: (state, action) => {
    return update(state, {
      questionLibrary: { libraryType: { $set: action.payload } },
    });
  },
  [DISABLE_SCROLL_EVENT]: (state, action) =>
    update(state, { isScrollEventDisabled: { $set: action.payload } }),
  [UPDATE_GRAPH_THEATER_MODE]: (state, action) =>
    update(state, {
      isGraphTheaterModeEnabled: { $set: action.payload },
    }),
  [UPDATE_PARENT_SUBMISSION_ID]: (state, action) =>
    update(state, {
      parentSubmissionId: { $set: action.payload },
    }),
  [UPDATE_AI_QUESTION_GENERATION]: (state, action) =>
    update(state, {
      isAIQuestionGenerationEnabled: { $set: action.payload },
    }),
  [UPDATE_GENERATING_AI_QUESTIONS]: (state, action) =>
    update(state, {
      generatingAIQuestions: { $set: action.payload },
    }),
  [RESET_REDUX_STATE]: state => update(state, { $set: initialState }),
  [UPDATE_QUESTION_TAG]: (state, action) => {
    const params = action.payload;

    _.map(_.keys(params), key => {
      state = update(state, {
        questionLibraryTags: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [AI_ENHANCE_QUESTION]: (state, action) => {
    return update(state, {
      aiEnhanceQuestion: { $set: action.payload },
    });
  },
  [AI_ENHANCE_PROMPTS]: (state, action) => {
    return update(state, {
      aiEnhancePrompts: { $set: action.payload },
    });
  },
  [UPDATE_VISIBLE_QUESTION_ASSISTANT_IDS]: (state, action) =>
    update(state, {
      visibleQuestionAssistantIds: { $set: action.payload },
    }),
  [UPDATE_QUESTION_THEATER_MODE]: (state, action) =>
    update(state, {
      isQuestionTheaterModeEnable: { $set: action.payload },
    }),
  [UPDATE_ENABLE_AUTO_SUBMIT]: (state, action) =>
    update(state, {
      enableAutoSubmit: { $set: action.payload },
    }),

  [UPDATE_EXTRACT_FROM_DOC]: (state, action) => {
    const { assessmentId, data } = action.payload;
    if (!assessmentId || !_.isObject(data)) return state;
    return update(state, {
      extractFromDoc: {
        $apply: (existing = {}) => {
          const prevData = _.defaults({}, existing[assessmentId], {
            isExtracting: false,
            progress: 0,
            runningProgress: 0,
          });
          return {
            ...existing,
            [assessmentId]: {
              ...prevData,
              ...data,
            },
          };
        },
      },
    });
  },
  [UPDATE_WORKSHEET_SIDEBAR]: (state, action) => {
    const payload = action.payload;
    if (!_.isObject(payload)) return state;

    _.map(_.keys(payload), key => {
      state = update(state, {
        sidebar: { [key]: { $set: payload[key] } },
      });
    });
    return state;
  },
  [UPDATE_AI_ASSESSMENT_TOOLS]: (state, action) => {
    const payload = action.payload;
    if (!_.isObject(payload)) return state;

    _.map(_.keys(payload), key => {
      state = update(state, {
        aiAssessmentTools: { [key]: { $set: payload[key] } },
      });
    });
    return state;
  },
  [UPDATE_ADD_QUESTIONS_IN_BETWEEN]: (state, action) => {
    const payload = action.payload;
    if (!_.isObject(payload)) return state;

    _.map(_.keys(payload), key => {
      state = update(state, {
        addQuestionsInBetween: { [key]: { $set: payload[key] } },
      });
    });
    return state;
  },
  [UPDATE_AI_PANEL]: (state, action) => {
    const payload = action.payload;
    if (!_.isObject(payload)) return state;

    _.map(_.keys(payload), key => {
      state = update(state, {
        aiPanel: { [key]: { $set: payload[key] } },
      });
    });
    return state;
  },
  [UPDATE_WORKSHEET_SUBMISSION_MODAL]: (state, action) => {
    const payload = action.payload;
    if (!_.isBoolean(payload)) return state;

    return update(state, { showWorksheetSubmissionModal: { $set: payload } });
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
